import axios from '../axios';
import { ApiUrl } from '../config';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put, delay, select } from 'redux-saga/effects';
import { setAdminUsers, setAdminUserStats, setAdminWorkspaces, setWorkspaceStats, setSelectedWorkspaceDetails, setWorkspaceActivity, setWorkspaceMembers, setWorkspaceContacts, setWorkspaceDeals, setWorkspaceAgents, setOwnerWorkspaces, setAccountAgents, setWorkspacePaymentHistory, setWorkspaceBranding, setDomainInstructions, setAdminSubscriptions, setSubscriptionStats, setSelectedSubscription, setPayments, setRevenueAnalytics, setMRRAnalytics, setPlansOverview, setAdminPlans, setAdminAgents, setAgentStats, setSelectedAgent, setAgentLogs, setAgentCalls, setPerformanceMetrics, setCostAnalytics, setSystemHealth, setSystemServices, setSystemMetrics, setSystemAlerts, setSystemPerformance, setAdminDashboard, setAdminLoading, setEmailTemplates, setTemplateStats, setActivityLogs, setActivityStats, setAgentPerformance, setUserWorkspaces, setUserBillingHistory, setUserSecurityLogs, setUserActivities } from './index';

// ===== USER MANAGEMENT SAGAS =====

function* getAdminUsers({ payload = {} }) {
  try {
    const params = new URLSearchParams({ page: payload['page'] || 1, limit: payload['limit'] || 20, ...(payload['search'] && { search: payload['search'] }), ...(payload['role'] && { role: payload['role'] }), ...(payload['status'] && { status: payload['status'] }) });

    const { error, response } = yield call(getCall, `/admin/users?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch users');
    yield put(setAdminUsers(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch admin users');
  }
}

function* getAdminUserStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/users/stats`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch user stats');
    yield put(setAdminUserStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch user statistics');
  }
}

function* exportAdminUsers({ payload = {} }) {
  try {
    const params = new URLSearchParams({ ...(payload['search'] && { search: payload['search'] }), ...(payload['role'] && { role: payload['role'] }), ...(payload['status'] && { status: payload['status'] }) });

    const { error, response } = yield call(getCall, `/admin/users/export?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to export users');
      return;
    }
    
    // Return the data to the caller instead of putting it in store
    if (payload['callback'] && typeof payload['callback'] === 'function') payload['callback'](response['data']['body']);
    
    EventBus.publish('success', `Successfully exported ${response['data']['body']['total']} users`);
  } catch (error) {
    EventBus.publish('error', 'Failed to export users');
    if (payload['callback'] && typeof payload['callback'] === 'function') payload['callback'](null, error);
  }
}

function* updateAdminUser({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating user...' }));
    const { error, response } = yield call(putCall, { path: `/admin/users/${payload['id']}`, payload: payload['updates'] });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update user');
    else {
      EventBus.publish('success', 'User updated successfully');
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update user');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* createAdminUser({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Creating user...' }));
    const { error, response } = yield call(postCall, { path: '/admin/users', payload: payload['userData'] });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create user');
    else {
      EventBus.publish('success', 'User created successfully');
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to create user');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* impersonateUser({ payload }) {
  try {
    const { error, response } = yield call(postCall, { path: `/admin/users/${payload['id']}/impersonate`, payload: {} });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to impersonate user');
    else {
      EventBus.publish('success', 'Impersonating user...');
      // Handle impersonation logic (e.g., update auth token, redirect)
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to impersonate user');
  }
}

function* exitImpersonation({ payload }) {
  try {
    // Use the original admin token for this request
    const originalToken = payload['originalToken'];
    
    if (!originalToken) {
      EventBus.publish('error', 'Original admin token not found. Cannot exit impersonation.');
      if (payload['callback']) payload['callback'](false);
      return;
    }
    
    // Make a direct fetch call with the admin token
    const response = yield call(fetch, `${ApiUrl}/admin/users/exit-impersonation`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `bearer ${originalToken}`, 'x-workspace-id': localStorage.getItem('workspaceId') || '' }, body: JSON.stringify({}) });
    
    if (!response['ok']) {
      const errorData = yield response.json();
      EventBus.publish('error', errorData?.['message'] || 'Failed to exit impersonation');
      if (payload['callback']) payload['callback'](false);
    } else {
      EventBus.publish('success', 'Exiting impersonation mode...');
      if (payload['callback']) payload['callback'](true);
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to exit impersonation');
    if (payload['callback']) payload['callback'](false);
  }
}

function* deleteAdminUser({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting user...' }));
    const { error } = yield call(deleteCall, `/admin/users/${payload['id']}`);
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete user');
    else {
      EventBus.publish('success', 'User has been deactivated successfully');
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to delete user');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* activateUser({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Activating user...' }));
    const { error } = yield call(postCall, { path: `/admin/users/${payload['id']}/activate`, payload: {} });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to activate user');
    else {
      EventBus.publish('success', 'User activated successfully');
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to activate user');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* deactivateUser({ payload }) {
  try {
    const action = payload['action'] || 'deactivate'; // 'suspend' or 'deactivate'
    const message = action === 'suspend' ? 'Suspending user...' : 'Deactivating user...';
    const successMessage = action === 'suspend' ? 'User paused successfully' : 'User deactivated successfully';
    const errorMessage = action === 'suspend' ? 'Failed to suspend user' : 'Failed to deactivate user';

    yield put(setAdminLoading({ isLoading: true, message }));
    const { error } = yield call(postCall, { path: `/admin/users/${payload['id']}/deactivate`, payload: { action } });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || errorMessage);
    else {
      EventBus.publish('success', successMessage);
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update user status');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getDeletedUsers({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Fetching deleted users...' }));
    const params = new URLSearchParams({
      page: payload?.page || 1,
      limit: payload?.limit || 50,
      ...(payload?.search && { search: payload.search })
    });

    const { error, response } = yield call(getCall, `/admin/users/deleted?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch deleted users');
    } else {
      yield put({
        type: 'SET_DELETED_USERS',
        payload: {
          deletedUsers: response['data']['body']['users'],
          deletedUsersPagination: response['data']['body']['pagination']
        }
      });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch deleted users');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* restoreUser({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Restoring user...' }));
    const { error, response } = yield call(postCall, {
      path: `/admin/users/${payload['id']}/restore`
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to restore user');
    } else {
      EventBus.publish('success', 'User reactivated successfully');
      // Refresh both active and deleted users
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] || {} });
      yield put({ type: 'GET_DELETED_USERS', payload: {} });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to restore user');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkUserAction({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: `Performing ${payload['action']} on users...` }));
    const { error } = yield call(postCall, { path: `/admin/users/bulk-action`, payload: { action: payload['action'], userIds: payload['userIds'], data: payload['data'] } });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to perform bulk action');
    else {
      EventBus.publish('success', 'Bulk action completed successfully');
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to perform bulk action');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== WORKSPACE MANAGEMENT SAGAS =====

function* getAvailableWorkspaces() {
  try {
    const { error, response } = yield call(getCall, '/admin/workspaces/available?activeOnly=true');
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch available workspaces');

    yield put({ type: 'SET_AVAILABLE_WORKSPACES', payload: response['data']['body']['workspaces'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch available workspaces');
  }
}

function* getWorkspacePlans() {
  try {
    const { error, response } = yield call(getCall, '/admin/workspaces/available-plans');
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace plans');

    const data = response['data']['body'];
    yield put({
      type: 'SET_WORKSPACE_PLANS',
      payload: {
        plans: data['plans'] || [],
        hasStripeConfigured: data['hasStripeConfigured'] || false,
        message: data['message'] || ''
      }
    });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace plans');
  }
}

function* getAdminWorkspaces({ payload = {} }) {
  try {
    const params = new URLSearchParams({ page: payload['page'] || 1, limit: payload['limit'] || 20, ...(payload['search'] && { search: payload['search'] }), ...(payload['status'] && { status: payload['status'] }), ...(payload['plan'] && { plan: payload['plan'] }), ...(payload['planCategory'] && { planCategory: payload['planCategory'] }), ...(payload['minCredits'] && { minCredits: payload['minCredits'] }) });

    const { error, response } = yield call(getCall, `/admin/workspaces?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspaces');
      if (payload['callback'] && typeof payload['callback'] === 'function') payload['callback'](null, error);
      return;
    }
    
    const data = response['data']['body'];
    yield put(setAdminWorkspaces(data));
    
    // If callback is provided, call it with the data
    if (payload['callback'] && typeof payload['callback'] === 'function') payload['callback'](data, null);
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspaces');
    if (payload['callback'] && typeof payload['callback'] === 'function') payload['callback'](null, error);
  }
}

function* updateAdminWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating workspace...' }));
    const { error } = yield call(putCall, { path: `/admin/workspaces/${payload['id']}`, payload: payload['updates'] });
    if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update workspace');
    else {
      EventBus.publish('success', 'Workspace updated successfully');
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* deleteAdminWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting workspace...' }));
    const { error } = yield call(deleteCall, `/admin/workspaces/${payload['id']}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to deactivate workspace');
    } else {
      EventBus.publish('success', 'Workspace deactivated successfully');
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to deactivate workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getDeletedWorkspaces({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Fetching deleted workspaces...' }));
    const params = new URLSearchParams({
      page: payload?.page || 1,
      limit: payload?.limit || 50,
      ...(payload?.search && { search: payload.search })
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/deleted?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch deleted workspaces');
    } else {
      yield put({
        type: 'SET_DELETED_WORKSPACES',
        payload: {
          deletedWorkspaces: response['data']['body']['workspaces'],
          deletedWorkspacesPagination: response['data']['body']['pagination']
        }
      });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch deleted workspaces');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* restoreWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Restoring workspace...' }));
    const { error, response } = yield call(postCall, {
      path: `/admin/workspaces/${payload['id']}/restore`
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to restore workspace');
    } else {
      EventBus.publish('success', 'Workspace reactivated successfully');
      // Refresh both active and deleted workspaces
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] || {} });
      yield put({ type: 'GET_DELETED_WORKSPACES', payload: {} });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to restore workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* createWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Creating workspace...' }));
    const { error, response } = yield call(postCall, {
      path: `/admin/workspaces`,
      payload: payload['data']
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create workspace');
    } else {
      // Check if invitation was sent and customize success message
      const successMessage = response?.['data']?.['body']?.['invitationSent']
        ? `Workspace created successfully! An invitation has been sent to ${response?.['data']?.['body']?.['invitedEmail']} with owner role.`
        : 'Workspace created successfully';
      EventBus.publish('success', successMessage);
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
      // Refresh subscription to update sidebar credits without triggering setup screen
      yield put({ type: 'REFRESH_SUBSCRIPTION' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to create workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* resetWorkspaceUsage({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Resetting usage...' }));
    const { error } = yield call(postCall, {
      path: `/admin/workspaces/${payload['id']}/reset-usage`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to reset usage');
    } else {
      EventBus.publish('success', 'Usage reset successfully');
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to reset usage');
    yield put(setAdminLoading({ isLoading: false }));
  }
}
function* getWorkspaceStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/stats`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace stats');
    yield put(setWorkspaceStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace statistics');
  }
}

function* exportAdminWorkspaces({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['plan'] && { plan: payload['plan'] }),
      ...(payload['planCategory'] && { planCategory: payload['planCategory'] }),
      ...(payload['minCredits'] && { minCredits: payload['minCredits'] })
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/export?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to export workspaces');
      return;
    }
    
    // Return the data to the caller instead of putting it in store
    if (payload['callback'] && typeof payload['callback'] === 'function') {
      payload['callback'](response['data']['body']);
    }
    
    EventBus.publish('success', `Successfully exported ${response['data']['body']['total']} workspaces`);
  } catch (error) {
    EventBus.publish('error', 'Failed to export workspaces');
    if (payload['callback'] && typeof payload['callback'] === 'function') {
      payload['callback'](null, error);
    }
  }
}

function* getWorkspaceById({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace details');
    yield put(setSelectedWorkspaceDetails(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace details');
  }
}

function* getWorkspaceActivity({ payload = {} }) {
  console.log('=== getWorkspaceActivity SAGA STARTED ===');
  console.log('Payload:', payload);

  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['type'] && { type: payload['type'] }),
      ...(payload['userId'] && { userId: payload['userId'] })
    });

    console.log(`Fetching: /admin/activity-logs/workspace/${payload['id']}?${params}`);

    const { error, response } = yield call(getCall, `/admin/activity-logs/workspace/${payload['id']}?${params}`);

    console.log('API Response received:', !!response);
    console.log('API Error:', !!error);

    if (error) {
      console.error('Error fetching workspace activity:', error);
      yield put(setWorkspaceActivity([]));
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace activity');
      return;
    }

    // Log the response structure
    console.log('Response structure:', {
      hasData: !!response?.data,
      hasBody: !!response?.data?.body,
      hasActivities: !!response?.data?.body?.activities
    });

    // Extract activities array from response body, matching getUserActivities pattern
    const activities = response?.['data']?.['body']?.['activities'] || [];

    console.log(`Workspace activities extracted: ${activities.length} items`);
    if (activities.length > 0) {
      console.log('First activity:', activities[0]);
    }

    // Dispatch the activities array directly
    console.log('Dispatching setWorkspaceActivity with activities array');
    yield put(setWorkspaceActivity(activities));
    console.log('=== getWorkspaceActivity SAGA COMPLETED ===');

  } catch (error) {
    console.error('=== getWorkspaceActivity SAGA ERROR ===');
    console.error('Error details:', error);
    yield put(setWorkspaceActivity([]));
    EventBus.publish('error', 'Failed to fetch workspace activity');
  }
}

function* suspendWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Suspending workspace...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/workspaces/${payload['id']}/suspend`, 
      payload: { reason: payload['reason'] || 'Admin action' } 
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to suspend workspace');
    } else {
      EventBus.publish('success', 'Workspace Pause successfully');
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to suspend workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* unsuspendWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Activating workspace...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/workspaces/${payload['id']}/unsuspend`, 
      payload: {} 
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to activate workspace');
    } else {
      EventBus.publish('success', 'Workspace activated successfully');
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to activate workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* changeWorkspacePlan({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Changing workspace plan...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/workspaces/${payload['id']}/change-plan`, 
      payload: { 
        planId: payload['planId'],
        targetPlan: payload['targetPlan'] 
      } 
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to change workspace plan');
    } else {
      EventBus.publish('success', `Workspace plan changed to ${payload['targetPlan']} successfully`);
      yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
      yield put({ type: 'GET_WORKSPACE_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to change workspace plan');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkWorkspaceAction({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: `Performing ${payload['action']} on workspaces...` }));

    // Get current workspaces to check their status
    const { adminWorkspaces } = yield select(state => state.Admin);

    // Process each workspace based on action
    const promises = payload['workspaceIds'].map(workspaceId => {
      if (payload['action'] === 'suspend') {
        // Check if workspace is deactivated - can't suspend deactivated workspaces
        const workspace = adminWorkspaces.find(w => w._id === workspaceId);
        const isDeactivated = workspace?.computedStatus === 'deactivated' || workspace?.isDeleted;

        if (isDeactivated) return Promise.resolve({ error: true, message: 'Cannot suspend deactivated workspace' }); return call(postCall, { path: `/admin/workspaces/${workspaceId}/suspend`, payload: { reason: 'Bulk admin action' }});
      } else if (payload['action'] === 'activate') {
        // Check if workspace is deactivated (deleted) or just suspended
        const workspace = adminWorkspaces.find(w => w._id === workspaceId);
        const isDeactivated = workspace?.computedStatus === 'deactivated' || workspace?.isDeleted;
        if (isDeactivated) return call(postCall, { path: `/admin/workspaces/${workspaceId}/restore`, payload: {} });
        else return call(postCall, { path: `/admin/workspaces/${workspaceId}/unsuspend`, payload: {} });
      } else if (payload['action'] === 'resetUsage') { return call(postCall, { path: `/admin/workspaces/${workspaceId}/reset-usage`,  payload: {} });
      } else if (payload['action'] === 'changePlan') {
        return call(postCall, { path: `/admin/workspaces/${workspaceId}/change-plan`, payload: { planId: payload['planId'], targetPlan: payload['planId'] === 'Starter_business_month' ? 'Starter' : 'Free'}});
      }
    });
    const results = yield all(promises);
    const failedCount = results.filter(r => r.error).length;
    const successCount = results.filter(r => !r.error).length;

    // Check if failed due to deactivated workspaces
    const deactivatedFailures = results.filter(r => r.error && r.message?.includes('deactivated')).length;

    if (failedCount > 0 && successCount > 0) {
      EventBus.publish('warning', `Action completed: ${successCount} succeeded, ${failedCount} failed`);
    } else if (failedCount > 0) {
      if (deactivatedFailures > 0 && payload['action'] === 'suspend') {
        EventBus.publish('error', 'Cannot suspend deactivated workspaces. Please activate them first.');
      } else {
        EventBus.publish('error', `Failed to ${payload['action']} workspaces`);
      }
    } else {
      let successMessage = '';
      switch(payload['action']) {
        case 'suspend':
          successMessage = `Successfully suspended ${successCount} workspace(s)`;
          break;
        case 'activate':
          successMessage = `Successfully activated ${successCount} workspace(s)`;
          break;
        case 'resetUsage':
          successMessage = `Successfully reset usage for ${successCount} workspace(s)`;
          break;
        case 'changePlan':
          successMessage = `Successfully changed plan for ${successCount} workspace(s)`;
          break;
        default:
          successMessage = `Successfully processed ${successCount} workspace(s)`;
      }
      EventBus.publish('success', successMessage);
    }
    
    yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
    yield put({ type: 'GET_WORKSPACE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', `Failed to ${payload['action']} workspaces`);
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// Workspace Details Sidebar Sagas
function* getWorkspaceMembers({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/members?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace members');
    yield put(setWorkspaceMembers(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace members');
  }
}

function* getWorkspaceContacts({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/contacts?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace contacts');
    yield put(setWorkspaceContacts(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace contacts');
  }
}

function* getWorkspaceDeals({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/deals?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace deals');
    yield put(setWorkspaceDeals(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace deals');
  }
}

function* getWorkspaceAgents({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/agents?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch workspace agents');
    yield put(setWorkspaceAgents(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch workspace agents');
  }
}

function* getOwnerWorkspaces({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/owner-workspaces?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch owner workspaces');
    yield put(setOwnerWorkspaces(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch owner workspaces');
  }
}

function* getAccountAgents({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 100
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/account-agents?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch account agents');
    yield put(setAccountAgents(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch account agents');
  }
}

function* getWorkspacePaymentHistory({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['id']}/payment-history?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch payment history');
    yield put(setWorkspacePaymentHistory(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch payment history');
  }
}

// ===== BILLING MANAGEMENT SAGAS =====

function* getAdminSubscriptions({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['plan'] && { plan: payload['plan'] })
    });

    const { error, response } = yield call(getCall, `/admin/billing/subscriptions?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch subscriptions');
    
    // Transform subscription data to ensure customerName and customerEmail are populated
    const subscriptionData = response['data']['body'];
    if (subscriptionData?.subscriptions) {
      subscriptionData.subscriptions = subscriptionData.subscriptions.map(sub => {
        // Ensure customerName and customerEmail are populated
        if (!sub.customerName && sub.workspaceId?.ownerId) {
          sub.customerName = `${sub.workspaceId.ownerId.firstName || ''} ${sub.workspaceId.ownerId.lastName || ''}`.trim() || 
                            sub.workspaceId.ownerId.email || 'Unknown';
        }
        if (!sub.customerEmail && sub.workspaceId?.ownerId) {
          sub.customerEmail = sub.workspaceId.ownerId.email || 'N/A';
        }
        // Fallback if no workspace owner data
        if (!sub.customerName) {
          sub.customerName = sub.workspaceId?.name || 'Unknown';
        }
        if (!sub.customerEmail) {
          sub.customerEmail = 'N/A';
        }
        
        // Map creditsBalance to creditBalance for consistency in frontend
        if (sub.creditsBalance !== undefined) {
          sub.creditBalance = sub.creditsBalance;
        }
        
        return sub;
      });
    }
    
    yield put(setAdminSubscriptions(subscriptionData));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch subscriptions');
  }
}

function* getSubscriptionStats({ payload = {} }) {
  try {
    const params = new URLSearchParams();
    if (payload['startDate']) params.append('startDate', payload['startDate']);
    if (payload['endDate']) params.append('endDate', payload['endDate']);
    if (payload['period']) params.append('period', payload['period']);
    
    const { error, response } = yield call(getCall, `/admin/billing/subscriptions/stats?${params.toString()}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch subscription stats');
    yield put(setSubscriptionStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch subscription statistics');
  }
}

function* getSubscriptionById({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/billing/subscriptions/${payload['id']}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch subscription');
    yield put(setSelectedSubscription(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch subscription details');
  }
}

function* cancelSubscription({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Cancelling subscription...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/billing/subscriptions/${payload['id']}/cancel`,
      payload: payload['data']
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to cancel subscription');
    } else {
      EventBus.publish('success', 'Subscription cancelled successfully');
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: payload['filters'] });
      yield put({ type: 'GET_SUBSCRIPTION_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to cancel subscription');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* reactivateSubscription({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Reactivating subscription...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/billing/subscriptions/${payload['id']}/reactivate`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to reactivate subscription');
    } else {
      EventBus.publish('success', 'Subscription reactivated successfully');
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: payload['filters'] });
      yield put({ type: 'GET_SUBSCRIPTION_STATS' });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to reactivate subscription');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* applyCredit({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Applying credit...' }));
    const { error } = yield call(postCall, { 
      path: `/admin/billing/subscriptions/${payload['id']}/credit`,
      payload: payload['data']
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to apply credit');
    } else {
      EventBus.publish('success', 'Credit applied successfully');
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: payload['filters'] });
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to apply credit');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// Fetch all subscriptions for export
function* getAllSubscriptionsForExport({ payload, callback }) {
  try {
    const params = new URLSearchParams({
      limit: 10000, // Get all records
      ...(payload?.search && { search: payload['search'] }),
      ...(payload?.status && { status: payload['status'] }),
      ...(payload?.plan && { plan: payload['plan'] })
    });

    const { error, response } = yield call(getCall, `/admin/billing/subscriptions?${params}`);
    if (error) {
      EventBus.publish('error', 'Failed to fetch all subscriptions for export');
      if (callback) callback(null);
      return;
    }
    
    let subscriptions = response['data']['body']['subscriptions'] || [];
    
    // Transform subscription data to ensure customerName and customerEmail are populated
    subscriptions = subscriptions.map(sub => {
      // Ensure customerName and customerEmail are populated
      if (!sub.customerName && sub.workspaceId?.ownerId) {
        sub.customerName = `${sub.workspaceId.ownerId.firstName || ''} ${sub.workspaceId.ownerId.lastName || ''}`.trim() || 
                          sub.workspaceId.ownerId.email || 'Unknown';
      }
      if (!sub.customerEmail && sub.workspaceId?.ownerId) {
        sub.customerEmail = sub.workspaceId.ownerId.email || 'N/A';
      }
      // Fallback if no workspace owner data
      if (!sub.customerName) {
        sub.customerName = sub.workspaceId?.name || 'Unknown';
      }
      if (!sub.customerEmail) {
        sub.customerEmail = 'N/A';
      }
      
      // Map creditsBalance to creditBalance for consistency in frontend
      if (sub.creditsBalance !== undefined) {
        sub.creditBalance = sub.creditsBalance;
      }
      
      return sub;
    });
    
    if (callback) callback(subscriptions);
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch subscriptions for export');
    if (callback) callback(null);
  }
}

// Bulk cancel subscriptions - process individually since bulk endpoint doesn't exist
function* bulkCancelSubscriptions({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Cancelling subscriptions...' }));
    const { subscriptionIds, reason } = payload;
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each subscription individually with a small delay
    for (const id of subscriptionIds) {
      const { error } = yield call(postCall, {
        path: `/admin/billing/subscriptions/${id}/cancel`,
        payload: { reason }
      });
      
      if (!error) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to cancel subscription ${id}`);
      }
      
      // Small delay to avoid overwhelming the server
      yield delay(100);
    }
    
    if (successCount > 0) {
      EventBus.publish('success', `Successfully cancelled ${successCount} subscription(s)`);
      if (errorCount > 0) {
        EventBus.publish('warning', `Failed to cancel ${errorCount} subscription(s)`);
      }
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: {} });
      yield put({ type: 'GET_SUBSCRIPTION_STATS' });
    } else {
      EventBus.publish('error', 'Failed to cancel subscriptions');
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to cancel subscriptions');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// Bulk reactivate subscriptions - process individually since bulk endpoint doesn't exist
function* bulkReactivateSubscriptions({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Reactivating subscriptions...' }));
    const { subscriptionIds } = payload;
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each subscription individually with a small delay
    for (const id of subscriptionIds) {
      const { error } = yield call(postCall, {
        path: `/admin/billing/subscriptions/${id}/reactivate`,
        payload: {}
      });
      
      if (!error) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to reactivate subscription ${id}`);
      }
      
      // Small delay to avoid overwhelming the server
      yield delay(100);
    }
    
    if (successCount > 0) {
      EventBus.publish('success', `Successfully reactivated ${successCount} subscription(s)`);
      if (errorCount > 0) {
        EventBus.publish('warning', `Failed to reactivate ${errorCount} subscription(s)`);
      }
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: {} });
      yield put({ type: 'GET_SUBSCRIPTION_STATS' });
    } else {
      EventBus.publish('error', 'Failed to reactivate subscriptions');
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to reactivate subscriptions');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// Bulk apply credits - process individually since bulk endpoint doesn't exist
function* bulkApplyCredits({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Applying credits...' }));
    const { subscriptionIds, amount, reason } = payload;
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each subscription individually with a small delay
    for (const id of subscriptionIds) {
      const { error } = yield call(postCall, {
        path: `/admin/billing/subscriptions/${id}/credit`,
        payload: { 
          amount: parseFloat(amount), // Backend expects dollars, it will convert to cents
          description: reason || 'Admin credit adjustment'
        }
      });
      
      if (!error) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to apply credit to subscription ${id}`);
      }
      
      // Small delay to avoid overwhelming the server
      yield delay(100);
    }
    
    if (successCount > 0) {
      EventBus.publish('success', `Successfully applied credits to ${successCount} subscription(s)`);
      if (errorCount > 0) {
        EventBus.publish('warning', `Failed to apply credits to ${errorCount} subscription(s)`);
      }
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: {} });
    } else {
      EventBus.publish('error', 'Failed to apply credits');
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to apply credits');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getPayments({ payload = {} }) {
  try {
    yield put(setAdminLoading(true));
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['type'] && { type: payload['type'] }),
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['workspaceId'] && { workspaceId: payload['workspaceId'] })
    });

    const { error, response } = yield call(getCall, `/admin/billing/payments?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch payments');
    
    // Enhanced response now includes workspace summary
    const responseData = response['data']['body'];
    yield put(setPayments(responseData));
    
    // Store workspace summary separately if it exists
    if (responseData.workspaceSummary) {
      yield put({ type: 'SET_WORKSPACE_SUMMARY', payload: responseData.workspaceSummary });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch payments');
  } finally {
    yield put(setAdminLoading(false));
  }
}

function* processPaymentRefund({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Processing refund...' }));
    
    const { error, response } = yield call(postCall, {
      path: `/admin/billing/payments/${payload['paymentId']}/refund`,
      payload: payload['data']
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to process refund');
    } else {
      EventBus.publish('success', 'Refund processed successfully');
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to process refund');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getRevenueAnalytics({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      period: payload['period'] || '12m',
      granularity: payload['granularity'] || 'monthly'
    });
    
    if (payload['startDate']) params.append('startDate', payload['startDate']);
    if (payload['endDate']) params.append('endDate', payload['endDate']);

    const { error, response } = yield call(getCall, `/admin/billing/analytics/revenue?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch revenue analytics');
    yield put(setRevenueAnalytics(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch revenue analytics');
  }
}

function* getMRRAnalytics({ payload = {} }) {
  try {
    const params = new URLSearchParams();
    if (payload['startDate']) params.append('startDate', payload['startDate']);
    if (payload['endDate']) params.append('endDate', payload['endDate']);
    if (payload['period']) params.append('period', payload['period']);
    
    const { error, response } = yield call(getCall, `/admin/billing/analytics/mrr?${params.toString()}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch MRR analytics');
    yield put(setMRRAnalytics(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch MRR analytics');
  }
}

function* getPlansOverview({ payload = {} }) {
  try {
    const params = new URLSearchParams();
    if (payload['startDate']) params.append('startDate', payload['startDate']);
    if (payload['endDate']) params.append('endDate', payload['endDate']);
    if (payload['period']) params.append('period', payload['period']);

    const { error, response } = yield call(getCall, `/admin/billing/analytics/plans-overview?${params.toString()}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch plans overview');
    yield put(setPlansOverview(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch plans overview');
  }
}

function* getBillingOverview({ payload = {} }) {
  try {
    console.log('📡 SAGA: getBillingOverview called with payload:', payload);

    const params = new URLSearchParams();
    if (payload['period']) params.append('period', payload['period']);
    if (payload['startDate']) params.append('startDate', payload['startDate']);
    if (payload['endDate']) params.append('endDate', payload['endDate']);

    const apiUrl = `/admin/billing/analytics/billing-overview?${params}`;
    console.log('📡 SAGA: Making API call to:', apiUrl);

    const { error, response } = yield call(getCall, apiUrl);

    if (error) {
      console.error('📡 SAGA: API call failed:', error);
      return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch billing overview');
    }

    console.log('📡 SAGA: API response received:', response?.data?.body);

    // Update multiple states with the comprehensive data including dynamic charts
    if (response['data']['body']) {
      const data = response['data']['body'];

      console.log('📡 SAGA: Processing response data:', {
        overview: data.overview,
        revenueGrowth: data.revenueGrowth,
        subscriptionMetrics: data.subscriptionMetrics,
        chartData: data.chartData
      });

      // Update revenue analytics with dynamic chart data
      const revenuePayload = {
        totalRevenue: data.overview?.totalRevenue || 0,
        breakdown: {
          subscription: data.overview?.monthlyRecurringRevenue || 0,
          credits: 0,
          overage: 0
        },
        growth: data.revenueGrowth?.growthRate || 0,
        // Dynamic chart data from API
        chartData: data.chartData?.revenueChart || {
          labels: [],
          subscriptionRevenue: [],
          onetimeRevenue: []
        }
      };

      console.log('📡 SAGA: Updating revenue analytics with:', revenuePayload);
      yield put(setRevenueAnalytics(revenuePayload));

      // Update MRR analytics with dynamic breakdown
      const mrrPayload = {
        currentMRR: data.overview?.monthlyRecurringRevenue || 0,
        previousMRR: data.revenueGrowth?.previousPeriod || 0,
        mrrGrowth: data.revenueGrowth?.growthRate || 0,
        newMRR: data.chartData?.mrrBreakdown?.newMRR || 0,
        churnedMRR: data.chartData?.mrrBreakdown?.churnedMRR || 0,
        expansionMRR: data.chartData?.mrrBreakdown?.expansionMRR || 0,
        netNewMRR: data.chartData?.mrrBreakdown?.netNewMRR || 0
      };

      console.log('📡 SAGA: Updating MRR analytics with:', mrrPayload);
      yield put(setMRRAnalytics(mrrPayload));

      // Update subscription stats
      yield put(setSubscriptionStats({
        activeCount: data.overview?.activeSubscribers || 0,
        totalCount: data.subscriptionMetrics?.total || 0,
        canceledCount: data.subscriptionMetrics?.canceled || 0,
        churnRate: data.overview?.churnRate || 0,
        averageValue: data.overview?.averageRevenuePerUser || 0,
        subscriptionGrowth: data.revenueGrowth?.growthRate || 0,
        totalUsers: data.overview?.totalUsers || 0,
        ltv: data.overview?.customerLifetimeValue || 0
      }));

      // Update plans overview with dynamic plan distribution
      if (data.chartData?.planDistribution) {
        const planData = data.chartData.planDistribution;
        const formattedPlans = {
          plans: planData.labels.map((label, index) => ({
            _id: `plan_${index}`,
            name: label,
            displayName: label,
            type: 'Agency Plan',
            metrics: {
              activeSubscribers: planData.data[index] || 0,
              monthlyRevenue: 0,
              totalRevenue: 0,
              averageRevenue: 0,
              lifetimeRevenue: 0,
              growthRate: 0,
              churnRate: 0,
              trialCount: 0
            }
          })),
          planDistribution: planData,
          // Include totals from overview data
          totals: {
            totalSubscribers: data.overview?.activeSubscribers || 0,
            totalRevenue: data.overview?.totalRevenue || 0,
            totalMonthlyRevenue: data.overview?.monthlyRecurringRevenue || 0,
            totalLifetimeRevenue: data.overview?.totalRevenue || 0
          }
        };

        yield put(setPlansOverview(formattedPlans));
      }
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch billing overview');
  }
}

function* getAgencyPlanPerformance({ payload = {} }) {
  try {
    const { error, response } = yield call(getCall, `/admin/billing/analytics/agency-plans`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agency plans');

    // Transform agency plans to match the expected format for plansOverview
    if (response['data']['body']) {
      const agencyPlans = response['data']['body'].plans || [];
      const summary = response['data']['body'].summary || response['data']['body'].totals || {};
      const formattedPlans = {
        plans: agencyPlans.map(plan => ({
          _id: plan.id,
          name: plan.name,
          displayName: plan.name,
          type: 'Agency Plan',  // Show "Agency Plan" as the type
          price: plan.price,
          interval: plan.interval,
          metrics: {
            activeSubscribers: plan.metrics?.activeSubscriptions || 0,
            monthlyRevenue: plan.metrics?.monthlyRevenue || 0,
            totalRevenue: plan.metrics?.totalRevenue || 0,
            averageRevenue: plan.metrics?.averageValue || 0,
            lifetimeRevenue: plan.metrics?.totalRevenue || 0,
            growthRate: 0,
            churnRate: 0,
            trialCount: 0
          },
          features: plan.features
        })),
        summary: summary,
        totals: summary  // Add totals for frontend compatibility
      };

      yield put(setPlansOverview(formattedPlans));
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agency plan performance');
  }
}

function* getUserAccessType() {
  try {
    const { error, response } = yield call(getCall, `/admin/billing/user-access`);

    if (error) {
      console.error('Failed to get user access type:', error);
      return;
    }

    if (response?.data?.body?.data) {
      const accessData = response.data.body.data;
      console.log('📡 SAGA: User access type received:', accessData);

      yield put({
        type: 'SET_USER_ACCESS_TYPE',
        payload: accessData
      });
    }
  } catch (error) {
    console.error('Error fetching user access type:', error);
  }
}

// ===== PLAN MANAGEMENT SAGAS =====

function* getAdminPlans({ payload }) {
  try {
    yield put(setAdminLoading(true));
    const params = new URLSearchParams();
    if (payload?.search) params.append('search', payload['search']);
    if (payload?.type) params.append('type', payload['type']);
    if (payload?.status) params.append('status', payload['status']);
    if (payload?.sortBy) params.append('sortBy', payload['sortBy']);
    if (payload?.sortOrder) params.append('sortOrder', payload['sortOrder']);
    
    const { error, response } = yield call(getCall, `/admin/billing/plans?${params.toString()}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch plans');
    yield put(setAdminPlans(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch plans');
  } finally {
    yield put(setAdminLoading(false));
  }
}

function* updatePlan({ payload }) {
  try {
    yield put(setAdminLoading(true));
    const { id, ...updateData } = payload;
    const { error, response } = yield call(putCall, { 
      path: `/admin/billing/plans/${id}`, 
      payload: updateData 
    });
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update plan');
    
    EventBus.publish('success', 'Plan updated successfully');
    // Refresh plans list
    yield put({ type: 'GET_ADMIN_PLANS' });
    return response['data']['body'];
  } catch (error) {
    EventBus.publish('error', 'Failed to update plan');
  } finally {
    yield put(setAdminLoading(false));
  }
}

function* togglePlanStatus({ payload }) {
  try {
    yield put(setAdminLoading(true));
    const { error, response } = yield call(postCall, { 
      path: `/admin/billing/plans/${payload['id']}/toggle-status`,
      payload: {}
    });
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to toggle plan status');
    
    EventBus.publish('success', response['data']['message']);
    // Refresh plans list
    yield put({ type: 'GET_ADMIN_PLANS' });
    return response['data']['body'];
  } catch (error) {
    EventBus.publish('error', 'Failed to toggle plan status');
  } finally {
    yield put(setAdminLoading(false));
  }
}

// ===== SUBSCRIPTION DETAILS SIDEBAR SAGAS =====

function* getSubscriptionPayments({ payload }) {
  try {
    yield put({ type: 'LOADING_SUBSCRIPTION_PAYMENTS' });
    const { error, response } = yield call(getCall, `/admin/billing/subscriptions/${payload['id']}/payments`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch payment history');
    yield put({ type: 'SET_SUBSCRIPTION_PAYMENTS', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch payment history');
  }
}

function* getSubscriptionActivity({ payload }) {
  try {
    yield put({ type: 'LOADING_SUBSCRIPTION_ACTIVITY' });
    const { error, response } = yield call(getCall, `/admin/billing/subscriptions/${payload['id']}/activity`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch activity');
    yield put({ type: 'SET_SUBSCRIPTION_ACTIVITY', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch subscription activity');
  }
}

function* getSubscriptionUsageHistory({ payload }) {
  try {
    yield put({ type: 'LOADING_SUBSCRIPTION_USAGE_HISTORY' });
    const { error, response } = yield call(getCall, `/admin/billing/subscriptions/${payload['id']}/usage-history`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch usage history');
    yield put({ type: 'SET_SUBSCRIPTION_USAGE_HISTORY', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch usage history');
  }
}

function* applySubscriptionCredit({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Applying credit...' }));
    const { error, response } = yield call(postCall, {
      path: `/admin/billing/subscriptions/${payload['id']}/credit`,
      payload: {
        amount: payload['amount'],
        description: payload['description'] || payload['reason'] || 'Admin credit adjustment'
      }
    });
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to apply credit');
    EventBus.publish('success', `Successfully applied $${payload['amount']} credit`);
    yield put(setAdminLoading({ isLoading: false }));
    // Refresh subscriptions list and specific subscription details
    yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: {} });
    yield put({ type: 'GET_SUBSCRIPTION_BY_ID', payload: { id: payload['id'] } });
  } catch (error) {
    EventBus.publish('error', 'Failed to apply credit');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== AGENT MANAGEMENT SAGAS =====

function* getAdminAgents({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['workspace'] && { workspace: payload['workspace'] })
    });

    const { error, response } = yield call(getCall, `/admin/agents?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agents');
    yield put(setAdminAgents(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agents');
  }
}

function* getAgentStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/agents/stats`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agent stats');
    yield put(setAgentStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent statistics');
  }
}

function* exportAdminAgents({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['workspace'] && { workspace: payload['workspace'] })
    });

    const { error, response } = yield call(getCall, `/admin/agents/export?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to export agents');
      return;
    }
    
    // Return the data to the caller instead of putting it in store
    if (payload['callback'] && typeof payload['callback'] === 'function') {
      payload['callback'](response['data']['body']);
    }
    
    EventBus.publish('success', `Successfully exported ${response['data']['body']['total']} agents`);
  } catch (error) {
    EventBus.publish('error', 'Failed to export agents');
    if (payload['callback'] && typeof payload['callback'] === 'function') {
      payload['callback'](null, error);
    }
  }
}

function* getAgentById({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/agents/${payload['id']}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agent');
    yield put(setSelectedAgent(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent details');
  }
}

function* activateAgent({ payload }) {
  try {
    const { error } = yield call(postCall, { 
      path: `/admin/agents/${payload['id']}/activate`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to activate agent');
    } else {
      EventBus.publish('success', 'Agent activated successfully');
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to activate agent');
  }
}

function* deactivateAgent({ payload }) {
  try {
    const { error } = yield call(postCall, { 
      path: `/admin/agents/${payload['id']}/deactivate`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to deactivate agent');
    } else {
      EventBus.publish('success', 'Agent deactivated successfully');
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to deactivate agent');
  }
}

function* bulkUpdateAgentStatus({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating agent statuses...' }));
    const { agentIds, status } = payload;
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each agent individually since bulk endpoint doesn't exist
    for (const agentId of agentIds) {
      const endpoint = status === 'active' 
        ? `/admin/agents/${agentId}/activate`
        : `/admin/agents/${agentId}/deactivate`;
      
      const { error } = yield call(postCall, {
        path: endpoint,
        payload: {}
      });
      
      if (!error) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to ${status === 'active' ? 'activate' : 'deactivate'} agent ${agentId}:`, error);
      }
      
      // Small delay to avoid overwhelming the server
      yield delay(100);
    }
    
    // Show results
    if (successCount > 0) {
      const actionText = status === 'active' ? 'activated' : 'deactivated';
      EventBus.publish('success', `Successfully ${actionText} ${successCount} agent(s)`);
      if (errorCount > 0) {
        EventBus.publish('warning', `Failed to ${actionText.slice(0, -1)} ${errorCount} agent(s)`);
      }
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    } else {
      const actionText = status === 'active' ? 'activate' : 'deactivate';
      EventBus.publish('error', `Failed to ${actionText} agents`);
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update agent statuses');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* startAgent({ payload }) {
  try {
    const { error } = yield call(postCall, { 
      path: `/admin/agents/${payload['id']}/start`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to start agent');
    } else {
      EventBus.publish('success', 'Agent started successfully');
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to start agent');
  }
}

function* stopAgent({ payload }) {
  try {
    const { error } = yield call(postCall, { 
      path: `/admin/agents/${payload['id']}/stop`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to stop agent');
    } else {
      EventBus.publish('success', 'Agent stopped successfully');
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to stop agent');
  }
}

function* restartAgent({ payload }) {
  try {
    const { error } = yield call(postCall, { 
      path: `/admin/agents/${payload['id']}/restart`,
      payload: {}
    });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to restart agent');
    } else {
      EventBus.publish('success', 'Agent restarted successfully');
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to restart agent');
  }
}

function* getAgentLogs({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50,
      ...(payload['level'] && { level: payload['level'] })
    });

    const { error, response } = yield call(getCall, `/admin/agents/${payload['id']}/logs?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agent logs');
    yield put(setAgentLogs(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent logs');
  }
}

function* getAgentCalls({ payload }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50,
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['from'] && { from: payload['from'] })
    });

    const { error, response } = yield call(getCall, `/admin/agents/${payload['id']}/calls?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agent calls');
    yield put(setAgentCalls(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent calls');
  }
}

function* bulkAgentAction({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: `Performing ${payload['action']} on agents...` }));
    const { action, agentIds } = payload;
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each agent individually since bulk endpoint doesn't exist
    for (const agentId of agentIds) {
      let endpoint = '';
      let requestPayload = {};
      
      switch (action) {
        case 'start':
          endpoint = `/admin/agents/${agentId}/start`;
          break;
        case 'stop':
          endpoint = `/admin/agents/${agentId}/stop`;
          break;
        case 'restart':
          endpoint = `/admin/agents/${agentId}/restart`;
          break;
        case 'delete':
          endpoint = `/admin/agents/${agentId}`;
          break;
        default:
          console.warn(`Unknown bulk action: ${action}`);
          continue;
      }
      
      let error;
      if (action === 'delete') {
        ({ error } = yield call(deleteCall, endpoint));
      } else {
        ({ error } = yield call(postCall, {
          path: endpoint,
          payload: requestPayload
        }));
      }
      
      if (!error) {
        successCount++;
      } else {
        errorCount++;
        console.error(`Failed to ${action} agent ${agentId}:`, error);
      }
      
      // Small delay to avoid overwhelming the server
      yield delay(100);
    }
    
    // Show results
    if (successCount > 0) {
      EventBus.publish('success', `Successfully ${action}ed ${successCount} agent(s)`);
      if (errorCount > 0) {
        EventBus.publish('warning', `Failed to ${action} ${errorCount} agent(s)`);
      }
      yield put({ type: 'GET_ADMIN_AGENTS', payload: payload['filters'] });
      yield put({ type: 'GET_AGENT_STATS' });
    } else {
      EventBus.publish('error', `Failed to ${action} agents`);
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to perform bulk action');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getPerformanceMetrics({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      period: payload['period'] || '7d'
    });

    const { error, response } = yield call(getCall, `/admin/agents/performance?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch performance metrics');
    yield put(setPerformanceMetrics(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch performance metrics');
  }
}

function* getCostAnalytics({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      period: payload['period'] || '30d'
    });

    const { error, response } = yield call(getCall, `/admin/agents/costs?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch cost analytics');
    yield put(setCostAnalytics(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch cost analytics');
  }
}

// ===== SYSTEM MONITORING SAGAS =====

function* getSystemHealth() {
  try {
    const { error, response } = yield call(getCall, `/admin/monitoring/health`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system health');
    yield put(setSystemHealth(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system health');
  }
}

function* getSystemServices() {
  try {
    const { error, response } = yield call(getCall, `/admin/monitoring/services`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system services');
    yield put(setSystemServices(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system services');
  }
}

function* getSystemMetrics({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      period: payload['period'] || '1h'
    });

    const { error, response } = yield call(getCall, `/admin/monitoring/metrics?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system metrics');
    yield put(setSystemMetrics(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system metrics');
  }
}

function* getSystemAlerts({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      status: payload['status'] || 'active'
    });

    const { error, response } = yield call(getCall, `/admin/monitoring/alerts?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system alerts');
    yield put(setSystemAlerts(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system alerts');
  }
}

function* getSystemPerformance() {
  try {
    const { error, response } = yield call(getCall, `/admin/monitoring/performance`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system performance');
    yield put(setSystemPerformance(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system performance');
  }
}

function* getAgentPerformance({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      period: payload['period'] || '7d'
    });

    const { error, response } = yield call(getCall, `/admin/monitoring/agent-performance?${params}`);
    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch agent performance');
    yield put(setAgentPerformance(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent performance');
  }
}

// ===== DASHBOARD SAGA =====

function* getAdminDashboard() {
  try {
    const [ userStatsRes, workspaceStatsRes, subscriptionStatsRes, agentStatsRes, revenueRes, systemHealthRes, activityRes, agentHealthRes ] = yield all([
      call(getCall, '/admin/users/stats'),
      call(getCall, '/admin/workspaces/stats'),
      call(getCall, '/admin/billing/subscriptions/stats'),
      call(getCall, '/admin/agents/stats'),
      call(getCall, '/admin/billing/analytics/revenue?period=30d'),
      call(getCall, '/admin/monitoring/health'),
      call(getCall, '/admin/activity-logs/dashboard?limit=10'),
      call(getCall, '/admin/monitoring/agent-health')
    ]);

    const dashboardData = {
      userStats: userStatsRes['response']?.['data']?.['body'] || {},
      revenueData: revenueRes['response']?.['data']?.['body'] || {},
      agentStats: agentStatsRes['response']?.['data']?.['body'] || {},
      systemHealth: systemHealthRes['response']?.['data']?.['body'] || {},
      workspaceStats: workspaceStatsRes['response']?.['data']?.['body'] || {},
      subscriptionStats: subscriptionStatsRes['response']?.['data']?.['body'] || {},
      recentActivity: activityRes['response']?.['data']?.['body'] || [],
      agentHealth: agentHealthRes['response']?.['data']?.['body'] || {}
    };

    yield put(setAdminDashboard(dashboardData));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch dashboard data');
  }
}

function* getAgentHealthOnly() {
  try {
    const { error, response } = yield call(getCall, '/admin/monitoring/agent-health');
    if (error) {
      EventBus.publish('error', 'Failed to fetch agent health');
    } else {
      // Update only the agent health in the existing dashboard data
      yield put(setAdminDashboard({
        agentHealth: response['data']['body'] || {}
      }));
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch agent health status');
  }
}

// ===== SECURITY & COMPLIANCE SAGA FUNCTIONS =====

function* getAuditLogs({ payload }) {
  try {
    const params = new URLSearchParams();
    if (payload?.page) params.append('page', payload['page']);
    if (payload?.action) params.append('action', payload['action']);
    if (payload?.resource) params.append('resource', payload['resource']);
    if (payload?.adminId) params.append('adminId', payload['adminId']);
    if (payload?.startDate) params.append('startDate', payload['startDate']);
    if (payload?.endDate) params.append('endDate', payload['endDate']);
    if (payload?.success !== undefined) params.append('success', payload['success']);

    const { response, error } = yield call(getCall, `/admin/security/audit-logs?${params}`);
    
    if (response) {
      yield put({
        type: 'SET_AUDIT_LOGS',
        payload: {
          logs: response['data']?.['body']?.['logs'] || [],
          pagination: response['data']?.['body']?.['pagination'] || {}
        }
      });
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to fetch audit logs');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to fetch audit logs');
  }
}

// ===== CREDIT MANAGEMENT SAGA FUNCTIONS =====

function* addCredits({ payload }) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/billing/credits/add',
      payload: payload
    });
    
    if (response) {
      const data = response?.data?.body || response?.data;
      EventBus.publish('success', data?.message || 'Credits added successfully');
      
      // Refresh credit history
      yield put({ type: 'GET_CREDIT_HISTORY', payload: {} });
      
      // Refresh subscriptions to show updated balance
      yield put({ type: 'GET_ADMIN_SUBSCRIPTIONS', payload: {} });
    } else if (error) {
      EventBus.publish('error', error?.response?.data?.message || 'Failed to add credits');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to add credits');
  }
}

// Zapmail (Done-For-You) revenue tab — orders list + stats/wallet balance
function* getZapmailBilling({ payload }) {
  try {
    const params = new URLSearchParams();
    if (payload?.['page']) params.append('page', payload['page']);
    if (payload?.['limit']) params.append('limit', payload['limit']);
    if (payload?.['status']) params.append('status', payload['status']);

    const { error, response } = yield call(getCall, `/admin/billing/zapmail?${params}`);
    if (error) {
      EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch Zapmail orders');
      yield put({ type: 'SET_ZAPMAIL_BILLING', payload: null });
      return;
    }
    const { error: statsError, response: statsResponse } = yield call(getCall, '/admin/billing/zapmail/stats');
    yield put({
      type: 'SET_ZAPMAIL_BILLING',
      payload: {
        orders: response?.data?.body?.orders || [],
        pagination: response?.data?.body?.pagination || { page: 1, limit: 25, total: 0, pages: 0 },
        stats: statsError ? {} : (statsResponse?.data?.body || {})
      }
    });
  } catch (error) {
    console.error('getZapmailBilling saga error:', error);
  }
}

function* lookupZapmailWorkspace({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/billing/zapmail/lookup?q=${encodeURIComponent(payload['q'])}`);
    if (error) {
      EventBus.publish('error', error?.response?.data?.message || 'Lookup failed');
      if (payload['callback']) payload['callback']([]);
      return;
    }
    if (payload['callback']) payload['callback'](response?.data?.body?.results || []);
  } catch (error) {
    console.error('lookupZapmailWorkspace saga error:', error);
    if (payload['callback']) payload['callback']([]);
  }
}

function* getCreditHistory({ payload }) {
  try {
    console.log('📡 SAGA: getCreditHistory called with payload:', payload);

    const params = new URLSearchParams();
    if (payload?.page) params.append('page', payload['page']);
    if (payload?.search) params.append('search', payload['search']);
    if (payload?.startDate) params.append('startDate', payload['startDate']);
    if (payload?.endDate) params.append('endDate', payload['endDate']);
    if (payload?.limit) params.append('limit', payload['limit']);

    const apiUrl = `/admin/billing/credits/history?${params}`;
    console.log('📡 SAGA: Making API call to:', apiUrl);

    const { response, error } = yield call(getCall, apiUrl);

    if (error) {
      console.error('📡 SAGA: API call failed with error:', error);
      console.error('📡 SAGA: Error response:', error?.response?.data);
      EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch credit history');
      return;
    }

    if (response) {
      console.log('📡 SAGA: API response received:', response?.data);
      const data = response?.data?.body || response?.data;
      yield put({
        type: 'SET_CREDIT_HISTORY',
        payload: {
          transactions: data?.transactions || [],
          stats: data?.stats || {},
          pagination: data?.pagination || { page: 1, limit: 20, total: 0, pages: 0 }
        }
      });
    } else {
      console.error('📡 SAGA: No response received');
      EventBus.publish('error', 'No response received from server');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to fetch credit history');
  }
}

function* getWorkspaceCredits() {
  try {
    const { response } = yield call(getCall, '/admin/billing/credits/workspace-credits');

    if (response && response.data) {
      const { workspaces, stats } = response.data.body || {};

      yield put({
        type: 'SET_WORKSPACE_CREDITS',
        payload: { workspaces: workspaces || [], stats: stats || {} }
      });
    } else {
      yield put({
        type: 'SET_WORKSPACE_CREDITS',
        payload: { workspaces: [], stats: {} }
      });
      EventBus.publish('error', 'No response received from server');
    }
  } catch (err) {
    console.error('❌ Error fetching workspace credits:', err);
    yield put({
      type: 'SET_WORKSPACE_CREDITS',
      payload: { workspaces: [], stats: {} }
    });
    EventBus.publish('error', err.message || 'Failed to fetch workspace credits');
  }
}

function* getAllWorkspaces() {
  try {
    // Get ALL workspaces without limit - fetch in batches if needed
    const allWorkspaces = [];
    const allSubscriptions = [];
    let page = 1;
    let hasMore = true;
    
    // Fetch all workspaces in batches
    while (hasMore) {
      const { response } = yield call(getCall, `/admin/workspaces?page=${page}&limit=100`);
      if (response && response.data?.body?.workspaces) {
        const workspaces = response.data.body.workspaces;
        allWorkspaces.push(...workspaces);
        
        // Check if there are more pages
        const pagination = response.data.body.pagination;
        if (!pagination || page >= pagination.pages || workspaces.length === 0) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    
    // Fetch all subscriptions
    page = 1;
    hasMore = true;
    while (hasMore) {
      const { response } = yield call(getCall, `/admin/billing/subscriptions?page=${page}&limit=100`);
      if (response && response.data?.body?.subscriptions) {
        const subscriptions = response.data.body.subscriptions;
        allSubscriptions.push(...subscriptions);
        
        const pagination = response.data.body.pagination;
        if (!pagination || page >= pagination.pages || subscriptions.length === 0) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }
    
    // Create a map of subscriptions by workspace ID
    const subscriptionMap = {};
    allSubscriptions.forEach(sub => {
      if (sub.workspaceId) {
        const workspaceId = sub.workspaceId._id || sub.workspaceId;
        subscriptionMap[workspaceId] = sub;
      }
    });
    
    // Combine workspaces with their subscriptions
    const workspacesWithSubscriptions = allWorkspaces.map(workspace => ({
      ...workspace,
      currentSubscription: subscriptionMap[workspace._id] || null,
      hasSubscription: !!subscriptionMap[workspace._id],
      creditsBalance: subscriptionMap[workspace._id]?.creditsBalance || 0
    }));
    
    console.log(`Loaded ${allWorkspaces.length} workspaces with ${allSubscriptions.length} subscriptions`);
    
    yield put({
      type: 'SET_ALL_WORKSPACES',
      payload: workspacesWithSubscriptions
    });
  } catch (err) {
    console.error('Error fetching workspaces:', err);
    EventBus.publish('error', 'Failed to fetch workspaces');
  }
}

function* getSecuritySettings() {
  try {
    const { response, error } = yield call(getCall, '/admin/security/settings');
    
    if (response) {
      const settings = response['data']?.['body'] || {};
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: settings['settings'] || {},
          apiKeys: settings['apiKeys'] || [],
          ipWhitelist: settings['ipWhitelist'] || []
        }
      });
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to fetch security settings');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to fetch security settings');
  }
}

function* updateSecuritySettings({ payload }) {
  try {
    const { response, error } = yield call(putCall, {
      path: '/admin/security/settings',
      payload
    });
    
    if (response) {
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: response['data']?.['body']?.['settings'] || {},
          apiKeys: response['data']?.['body']?.['apiKeys'] || [],
          ipWhitelist: response['data']?.['body']?.['ipWhitelist'] || []
        }
      });
      EventBus.publish('success', 'Security settings updated successfully');
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to update security settings');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to update security settings');
  }
}

function* createApiKeySaga({ payload }) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/security/api-keys',
      payload
    });

    if (response) {
      const body = response['data']?.['body'] || {};
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: undefined,
          apiKeys: body['apiKeys'] || [],
          ipWhitelist: undefined
        }
      });
      EventBus.publish('success', 'API key generated successfully');
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to create API key');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to create API key');
  }
}

function* revokeApiKeySaga({ payload }) {
  try {
    const { key } = payload;
    const { response, error } = yield call(deleteCall, `/admin/security/api-keys/${key}`);
    if (response) {
      const body = response['data']?.['body'] || {};
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: undefined,
          apiKeys: body['apiKeys'] || [],
          ipWhitelist: undefined
        }
      });
      EventBus.publish('success', 'API key revoked');
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to revoke API key');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to revoke API key');
  }
}

function* addWhitelistIpSaga({ payload }) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/security/ip-whitelist',
      payload
    });
    if (response) {
      const body = response['data']?.['body'] || {};
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: undefined,
          apiKeys: undefined,
          ipWhitelist: body['ipWhitelist'] || []
        }
      });
      EventBus.publish('success', 'IP added to whitelist');
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to add IP to whitelist');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to add IP to whitelist');
  }
}

function* removeWhitelistIpSaga({ payload }) {
  try {
    const { ip } = payload;
    const { response, error } = yield call(deleteCall, `/admin/security/ip-whitelist/${ip}`);
    if (response) {
      const body = response['data']?.['body'] || {};
      yield put({
        type: 'SET_SECURITY_SETTINGS',
        payload: {
          settings: undefined,
          apiKeys: undefined,
          ipWhitelist: body['ipWhitelist'] || []
        }
      });
      EventBus.publish('success', 'IP removed from whitelist');
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to remove IP from whitelist');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to remove IP from whitelist');
  }
}

// Compliance Sagas
function* getComplianceStatus() {
  try {
    const { response, error } = yield call(getCall, '/admin/security/compliance/status');
    
    if (response) {
      const compliance = response['data']?.['body']?.['compliance'] || {};
      yield put({
        type: 'SET_COMPLIANCE_STATUS',
        payload: { compliance }
      });
    } else {
      console.error('Failed to get compliance status:', error);
    }
  } catch (error) {
    console.error('Error getting compliance status:', error);
  }
}

function* updateGDPRCompliance(action) {
  try {
    const { response, error } = yield call(putCall, {
      path: '/admin/security/compliance/gdpr',
      payload: { gdprSettings: action.payload }
    });
    
    if (response) {
      yield put({ type: 'GET_COMPLIANCE_STATUS' });
      EventBus.publish('success', 'GDPR compliance settings updated');
    } else {
      EventBus.publish('error', 'Failed to update GDPR compliance');
    }
  } catch (error) {
    console.error('Error updating GDPR compliance:', error);
    EventBus.publish('error', 'Error updating GDPR compliance');
  }
}

function* updateDataRetention(action) {
  try {
    const { response, error } = yield call(putCall, {
      path: '/admin/security/compliance/retention',
      payload: { policies: action.payload }
    });
    
    if (response) {
      yield put({ type: 'GET_COMPLIANCE_STATUS' });
      EventBus.publish('success', 'Data retention policies updated');
    } else {
      EventBus.publish('error', 'Failed to update data retention');
    }
  } catch (error) {
    console.error('Error updating data retention:', error);
    EventBus.publish('error', 'Error updating data retention');
  }
}

function* getDataRequestsSaga() {
  try {
    const { response, error } = yield call(getCall, '/admin/security/compliance/data-requests');
    
    if (response) {
      const requests = response['data']?.['body']?.['requests'] || [];
      yield put({
        type: 'SET_DATA_REQUESTS',
        payload: { requests }
      });
    } else {
      console.error('Failed to get data requests:', error);
    }
  } catch (error) {
    console.error('Error getting data requests:', error);
  }
}

function* createDataRequest(action) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/security/compliance/data-requests',
      payload: action.payload
    });
    
    if (response) {
      yield put({ type: 'GET_DATA_REQUESTS' });
      EventBus.publish('success', 'Data request created successfully');
    } else {
      EventBus.publish('error', 'Failed to create data request');
    }
  } catch (error) {
    console.error('Error creating data request:', error);
    EventBus.publish('error', 'Error creating data request');
  }
}

function* processDataRequestSaga(action) {
  try {
    const { id, status, notes } = action.payload;
    const { response, error } = yield call(putCall, {
      path: `/admin/security/compliance/data-requests/${id}`,
      payload: { status, notes }
    });
    
    if (response) {
      yield put({ type: 'GET_DATA_REQUESTS' });
      EventBus.publish('success', 'Data request processed successfully');
    } else {
      EventBus.publish('error', 'Failed to process data request');
    }
  } catch (error) {
    console.error('Error processing data request:', error);
    EventBus.publish('error', 'Error processing data request');
  }
}

function* generateComplianceReport(action) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/security/compliance/reports/generate',
      payload: { type: action.payload['type'] }
    });
    
    if (response) {
      const report = response['data']?.['body']?.['report'];
      
      // Download the report as JSON
      const dataStr = JSON.stringify(report, null, 2);
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      
      const exportFileDefaultName = `compliance-report-${action.payload['type']}-${new Date().toISOString().split('T')[0]}.json`;
      
      const linkElement = document.createElement('a');
      linkElement.setAttribute('href', dataUri);
      linkElement.setAttribute('download', exportFileDefaultName);
      linkElement.click();
      
      EventBus.publish('success', 'Compliance report generated successfully');
    } else {
      EventBus.publish('error', 'Failed to generate compliance report');
    }
  } catch (error) {
    console.error('Error generating compliance report:', error);
    EventBus.publish('error', 'Error generating compliance report');
  }
}

function* getAnalyticsData({ payload }) {
  try {
    const params = new URLSearchParams();
    if (payload?.dateRange?.start) params.append('startDate', payload['dateRange']['start']);
    if (payload?.dateRange?.end) params.append('endDate', payload['dateRange']['end']);
    if (payload?.metrics) params.append('metrics', payload['metrics'].join(','));

    // Fetch main analytics data
    const { response, error } = yield call(getCall, `/admin/analytics?${params}`);
    
    if (response) {
      const analyticsData = response['data']?.['body'] || {};
      
      // Also fetch revenue analytics and performance data
      const [revenueResponse, performanceResponse] = yield all([
        call(getCall, `/admin/analytics/revenue?${params}`),
        call(getCall, `/admin/analytics/performance?${params}`)
      ]);
      
      // Merge all analytics data
      const mergedData = {
        ...analyticsData,
        revenueDetails: revenueResponse?.response?.['data']?.['body'] || {},
        performanceDetails: performanceResponse?.response?.['data']?.['body'] || {}
      };
      
      // Update MRR data if available from revenue details
      if (revenueResponse?.response?.['data']?.['body']?.mrr) {
        mergedData.mrr = revenueResponse.response['data']['body'].mrr;
      }
      
      // Update revenue by source if available
      if (revenueResponse?.response?.['data']?.['body']?.revenueBySource) {
        mergedData.revenueBySource = revenueResponse.response['data']['body'].revenueBySource;
      }
      
      // Update funnel data from performance details
      if (performanceResponse?.response?.['data']?.['body']?.funnel) {
        mergedData.funnel = performanceResponse.response['data']['body'].funnel;
      }
      
      // Update agent performance from performance details
      if (performanceResponse?.response?.['data']?.['body']?.agentPerformance) {
        mergedData.agentPerformance = performanceResponse.response['data']['body'].agentPerformance;
      }
      
      yield put({
        type: 'SET_ANALYTICS_DATA',
        payload: mergedData
      });
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to fetch analytics data');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to fetch analytics data');
  }
}

function* getCustomReport({ payload }) {
  try {
    // Don't use global loading state - report generation is handled separately
    const { response, error } = yield call(postCall, {
      path: '/admin/analytics/reports',
      payload
    });
    
    if (response) {
      const report = response['data']?.['body']?.['report'];
      const reports = response['data']?.['body']?.['reports'] || [];
      
      // Update the reports list
      yield put({
        type: 'SET_CUSTOM_REPORTS',
        payload: reports
      });
      
      // Automatically download the generated report
      if (report && report.data) {
        // Create and download text file directly
        const exportResponse = yield call(postCall, {
          path: '/admin/analytics/export',
          payload: { 
            format: 'txt',
            data: report.data,
            reportId: report.id
          }
        });
        
        if (exportResponse.response) {
          const textContent = exportResponse.response['data']?.['body']?.['textContent'];
          const filename = exportResponse.response['data']?.['body']?.['filename'] || `report-${Date.now()}.txt`;
          
          if (textContent) {
            // Create and download text file
            const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            
            EventBus.publish('success', 'Report downloaded successfully');
          }
        }
      }
      
      yield put({ type: 'REPORT_GENERATION_COMPLETE' });
    } else {
      yield put({ type: 'REPORT_GENERATION_FAILED' });
      EventBus.publish('error', error?.response?.data?.body || 'Failed to generate custom report');
    }
  } catch (err) {
    yield put({ type: 'REPORT_GENERATION_FAILED' });
    EventBus.publish('error', 'Failed to generate custom report');
  }
}


function* getReports() {
  try {
    const { response, error } = yield call(getCall, '/admin/analytics/reports');
    
    if (response) {
      const reports = response['data']?.['body'] || [];
      yield put({
        type: 'SET_CUSTOM_REPORTS',
        payload: reports
      });
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to fetch reports');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to fetch reports');
  }
}

function* exportReport({ payload }) {
  try {
    const { response, error } = yield call(postCall, {
      path: '/admin/analytics/export',
      payload: { ...payload, format: 'txt' }
    });
    
    if (response) {
      const textContent = response['data']?.['body']?.['textContent'];
      const filename = response['data']?.['body']?.['filename'] || `report-${Date.now()}.txt`;
      const base64Data = response['data']?.['body']?.['data'];
      
      if (textContent) {
        // Create and download text file
        const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        EventBus.publish('success', 'Report downloaded successfully');
      } else if (base64Data) {
        // Fallback to base64 download
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        EventBus.publish('success', 'Report downloaded successfully');
      }
    } else {
      EventBus.publish('error', error?.response?.data?.body || 'Failed to export report');
    }
  } catch (err) {
    EventBus.publish('error', 'Failed to export report');
  }
}

// ===== EMAIL TEMPLATES SAGAS =====

function* getEmailTemplates({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['category'] && { category: payload['category'] }),
      ...(payload['status'] && { status: payload['status'] })
    });

    const { error, response } = yield call(getCall, `/admin/email-templates?${params}`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch email templates');
      return;
    }
    
    yield put(setEmailTemplates(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch email templates');
  }
}

function* getTemplateStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/email-templates/stats`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch template stats');
      return;
    }
    
    yield put(setTemplateStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch template statistics');
  }
}

function* createEmailTemplate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Creating template...' }));
    
    const { error, response } = yield call(postCall, {
      path: `/admin/email-templates`,
      payload: payload['template']
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create template');
    } else {
      EventBus.publish('success', 'Template created successfully');
      yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
      yield put({ type: 'GET_TEMPLATE_STATS' });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to create template');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* updateEmailTemplate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating template...' }));
    
    const { error, response } = yield call(putCall, {
      path: `/admin/email-templates/${payload['id']}`,
      payload: payload['updates']
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update template');
    } else {
      EventBus.publish('success', 'Template updated successfully');
      yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update template');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* deleteEmailTemplate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting template...' }));
    
    const { error } = yield call(deleteCall, `/admin/email-templates/${payload['id']}`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete template');
    } else {
      EventBus.publish('success', 'Template deleted successfully');
      yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
      yield put({ type: 'GET_TEMPLATE_STATS' });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to delete template');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* activateTemplate({ payload }) {
  try {
    const { error } = yield call(postCall, {
      path: `/admin/email-templates/${payload['id']}/activate`,
      payload: {}
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to activate template');
    } else {
      EventBus.publish('success', 'Template activated successfully');
      yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to activate template');
  }
}

function* deactivateTemplate({ payload }) {
  try {
    const { error } = yield call(postCall, {
      path: `/admin/email-templates/${payload['id']}/deactivate`,
      payload: {}
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to deactivate template');
    } else {
      EventBus.publish('success', 'Template deactivated successfully');
      yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to deactivate template');
  }
}

function* duplicateTemplate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Duplicating template...' }));
    // Mock API call for now
    EventBus.publish('success', 'Template duplicated successfully');
    yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to duplicate template');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkActivateTemplates({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Activating templates...' }));
    // Mock API call for now
    EventBus.publish('success', `${payload['templateIds'].length} templates activated successfully`);
    yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    yield put({ type: 'GET_TEMPLATE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to activate templates');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkDeactivateTemplates({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deactivating templates...' }));
    // Mock API call for now
    EventBus.publish('success', `${payload['templateIds'].length} templates deactivated successfully`);
    yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    yield put({ type: 'GET_TEMPLATE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to deactivate templates');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkDeleteTemplates({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting templates...' }));
    // Mock API call for now
    EventBus.publish('success', `${payload['templateIds'].length} templates deleted successfully`);
    yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    yield put({ type: 'GET_TEMPLATE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to delete templates');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkChangeTemplateCategory({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating categories...' }));
    // Mock API call for now
    EventBus.publish('success', 'Template categories updated successfully');
    yield put({ type: 'GET_EMAIL_TEMPLATES', payload: payload['filters'] });
    yield put({ type: 'GET_TEMPLATE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update categories');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkExportTemplates({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Exporting templates...' }));
    // Mock export for now
    EventBus.publish('success', 'Templates exported successfully');
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to export templates');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== ACTIVITY LOGS SAGAS =====

function* getActivityLogs({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 50,
      ...(payload['user'] && { user: payload['user'] }),
      ...(payload['action'] && { action: payload['action'] }),
      ...(payload['resource'] && { resource: payload['resource'] }),
      ...(payload['dateFrom'] && { dateFrom: payload['dateFrom'] }),
      ...(payload['dateTo'] && { dateTo: payload['dateTo'] })
    });

    const { error, response } = yield call(getCall, `/admin/activity-logs?${params}`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch activity logs');
      return;
    }
    
    yield put(setActivityLogs(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch activity logs');
  }
}

function* getActivityStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/activity-logs/stats`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch activity stats');
      return;
    }
    
    yield put(setActivityStats(response['data']['body']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch activity statistics');
  }
}

// Fetch a specific user's activities (dashboard-style) for preview in Users table
function* getUserActivities({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 10,
      ...(payload['type'] && { type: payload['type'] })
    });

    const { error, response } = yield call(getCall, `/admin/users/${payload['userId']}/activities?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch user activity');
      return;
    }
    // Use new setUserActivities action
    yield put(setUserActivities(response['data']['body']?.['activities'] || []));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch user activity');
  }
}

// Get user workspaces
function* getUserWorkspaces({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/users/${payload['userId']}/workspaces`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch user workspaces');
      return;
    }
    yield put(setUserWorkspaces(response['data']['body']?.['workspaces'] || []));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch user workspaces');
  }
}

// Get user billing history
function* getUserBillingHistory({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/users/${payload['userId']}/billing`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch user billing');
      return;
    }
    yield put(setUserBillingHistory(response['data']['body'] || {}));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch user billing');
  }
}

// Get user security logs
function* getUserSecurityLogs({ payload }) {
  try {
    const params = new URLSearchParams({
      limit: payload['limit'] || 15
    });

    const { error, response } = yield call(getCall, `/admin/users/${payload['userId']}/security-logs?${params}`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch user security logs');
      return;
    }
    yield put(setUserSecurityLogs(response['data']['body']?.['logs'] || []));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch user security logs');
  }
}

function* clearOldLogs({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Clearing old logs...' }));
    // Mock API call for now
    EventBus.publish('success', 'Old logs cleared successfully');
    yield put({ type: 'GET_ACTIVITY_LOGS', payload: payload['filters'] });
    yield put({ type: 'GET_ACTIVITY_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to clear old logs');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* archiveLogs({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Archiving logs...' }));
    // Mock API call for now
    EventBus.publish('success', 'Logs archived successfully');
    yield put({ type: 'GET_ACTIVITY_LOGS', payload: payload['filters'] });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to archive logs');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== SYSTEM CONFIGURATION SAGAS =====

function* getSystemConfig() {
  try {
    const { error, response } = yield call(getCall, `/admin/system-config`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch system configuration');
      return;
    }
    
    yield put({ type: 'SET_SYSTEM_CONFIG', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch system configuration');
  }
}

function* saveSystemConfig({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Saving configuration...' }));
    
    const { error, response } = yield call(putCall, {
      path: `/admin/system-config`,
      payload: payload['config']
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to save configuration');
    } else {
      EventBus.publish('success', 'Configuration saved successfully');
      yield put({ type: 'SET_SYSTEM_CONFIG', payload: response['data']['body'] });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to save configuration');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* resetConfigCategory({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Resetting configuration...' }));
    // Mock API call for now
    EventBus.publish('success', `${payload['category']} configuration reset to defaults`);
    yield put({ type: 'GET_SYSTEM_CONFIG' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to reset configuration');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* testServiceConnection({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: `Testing ${payload['service']} connection...` }));
    
    const { error, response } = yield call(postCall, {
      path: `/admin/system-config/test-connection`,
      payload: { service: payload['service'], config: payload['config'] }
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || `Failed to connect to ${payload['service']}`);
    } else {
      EventBus.publish('success', response['data']['message'] || `${payload['service']} connection successful`);
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', `Failed to connect to ${payload['service']}`);
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== BACKUP & RESTORE SAGAS =====

function* getBackups({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['type'] && { type: payload['type'] }),
      ...(payload['status'] && { status: payload['status'] })
    });
    
    const { error, response } = yield call(getCall, `/admin/backup-restore/backups?${params}`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch backups');
      return;
    }
    
    yield put({ type: 'SET_BACKUPS', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch backups');
  }
}

function* getBackupStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/backup-restore/backups/stats`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch backup stats');
      return;
    }
    
    yield put({ type: 'SET_BACKUP_STATS', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch backup statistics');
  }
}

function* getRestoreHistory({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20
    });
    
    const { error, response } = yield call(getCall, `/admin/backup-restore/restore-history?${params}`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch restore history');
      return;
    }
    
    yield put({ type: 'SET_RESTORE_HISTORY', payload: response['data']['body'] });
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch restore history');
  }
}

function* createBackup({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Creating backup...' }));
    
    const { error, response } = yield call(postCall, {
      path: `/admin/backup-restore/backups/create`,
      payload: payload['options'] || {}
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create backup');
    } else {
      EventBus.publish('success', 'Backup creation started');
      yield put({ type: 'GET_BACKUPS' });
      yield put({ type: 'GET_BACKUP_STATS' });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to create backup');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* restoreBackup({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Restoring backup...' }));
    // Mock API call for now
    EventBus.publish('success', 'Restore process started');
    yield put({ type: 'GET_RESTORE_HISTORY' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to restore backup');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* deleteBackup({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting backup...' }));
    // Mock API call for now
    EventBus.publish('success', 'Backup deleted successfully');
    yield put({ type: 'GET_BACKUPS' });
    yield put({ type: 'GET_BACKUP_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to delete backup');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* downloadBackup({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Preparing download...' }));
    // Mock download for now
    EventBus.publish('success', 'Download started');
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to download backup');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* uploadBackup({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Uploading backup...' }));
    // Mock upload for now
    EventBus.publish('success', 'Backup uploaded successfully');
    yield put({ type: 'GET_BACKUPS' });
    yield put({ type: 'GET_BACKUP_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to upload backup');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* cleanupOldBackups({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Cleaning up old backups...' }));
    // Mock API call for now
    EventBus.publish('success', 'Old backups cleaned up successfully');
    yield put({ type: 'GET_BACKUPS' });
    yield put({ type: 'GET_BACKUP_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to cleanup backups');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* verifyAllBackups() {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Verifying backups...' }));
    // Mock API call for now
    EventBus.publish('success', 'All backups verified successfully');
    yield put({ type: 'GET_BACKUPS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to verify backups');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* rebuildBackupIndex() {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Rebuilding backup index...' }));
    // Mock API call for now
    EventBus.publish('success', 'Backup index rebuilt successfully');
    yield put({ type: 'GET_BACKUPS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to rebuild backup index');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== ADDITIONAL BULK ACTIONS SAGAS =====

function* deleteBulkUsers({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting users...' }));
    
    const { error, response } = yield call(postCall, {
      path: `/admin/users/bulk-action`,
      payload: {
        action: 'delete',
        userIds: payload['userIds']
      }
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete users');
    } else {
      const affected = response['data']?.['body']?.['affected'] || payload['userIds'].length;
      EventBus.publish('success', `${affected} user(s) deleted successfully`);
      yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
      yield put({ type: 'GET_ADMIN_USER_STATS' });
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to delete users');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* sendBulkEmail({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Sending emails...' }));
    // Mock API call for now
    EventBus.publish('success', `Email sent to ${payload['userIds'].length} users`);
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to send emails');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkEmailSubscriptions({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Updating email subscriptions...' }));
    // Mock API call for now
    EventBus.publish('success', 'Email subscriptions updated successfully');
    yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update email subscriptions');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkResetWorkspaceUsage({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Resetting workspace usage...' }));
    // Mock API call for now
    EventBus.publish('success', `Usage reset for ${payload['workspaceIds'].length} workspaces`);
    yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
    yield put({ type: 'GET_WORKSPACE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to reset workspace usage');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkDeleteWorkspaces({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Deleting workspaces...' }));
    
    // Delete each workspace
    const promises = payload['ids'].map(workspaceId => 
      call(deleteCall, `/admin/workspaces/${workspaceId}`)
    );
    
    const results = yield all(promises);
    const failedCount = results.filter(r => r.error).length;
    const successCount = results.filter(r => !r.error).length;
    
    if (failedCount > 0 && successCount > 0) {
      EventBus.publish('warning', `Deactivated ${successCount} workspace(s), ${failedCount} failed`);
    } else if (failedCount > 0) {
      EventBus.publish('error', 'Failed to deactivate some workspaces');
    } else {
      EventBus.publish('success', `Successfully deactivated ${successCount} workspace(s)`);
    }

    yield put({ type: 'GET_ADMIN_WORKSPACES', payload: payload['filters'] });
    yield put({ type: 'GET_WORKSPACE_STATS' });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to deactivate workspaces');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* bulkAssignWorkspace({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Assigning workspace...' }));
    // Mock API call for now
    EventBus.publish('success', 'Workspace assigned successfully');
    yield put({ type: 'GET_ADMIN_USERS', payload: payload['filters'] });
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to assign workspace');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getNotificationHistory({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['sortBy'] && { sortBy: payload['sortBy'] }),
      ...(payload['sortOrder'] && { sortOrder: payload['sortOrder'] })
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/notification-history?${params}`);
    if (error) {
      console.error('Error fetching notification history:', error);
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch notification history');
      if (payload['callback']) payload['callback'](null, error);
    } else {
      console.log('Notification history response:', response);
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
  } catch (error) {
    console.error('Failed to fetch notification history:', error);
    EventBus.publish('error', 'Failed to fetch notification history');
    if (payload['callback']) payload['callback'](null, error);
  }
}

function* sendWorkspaceNotification({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Sending notification...' }));

    const { workspaceIds, subject, message } = payload;
    const { error, response } = yield call(postCall, {
      path: '/admin/workspaces/send-notification',
      payload: {
        workspaceIds,
        subject,
        message
      }
    });
    
    if (error) {
      EventBus.publish('error', error?.response?.data?.message || 'Failed to send notification');
    } else {
      EventBus.publish('success', response?.data?.message || 'Notification sent successfully');
    }
    
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to send notification');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

// ===== WORKSPACE BRANDING SAGAS =====

function* getWorkspaceBranding({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['workspaceId']}/branding`);
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch branding settings');
      return;
    }
    yield put(setWorkspaceBranding(response['data']['body']['branding']));
  } catch (error) {
    EventBus.publish('error', 'Failed to fetch branding settings');
  }
}

function* updateWorkspaceBranding({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Saving branding settings...' }));
    const { error, response } = yield call(putCall, { path: `/admin/workspaces/${payload['workspaceId']}/branding`, payload: payload['branding'] });
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update branding settings');
    } else {
      EventBus.publish('success', 'Branding settings updated successfully');
      yield put(setWorkspaceBranding(response['data']['body']['branding']));
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to update branding settings');
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* uploadBrandingAsset({ payload }) {
  try {
    const { workspaceId, field, file, onProgress, callback } = payload;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('field', field);
    
    const { error, response } = yield call(() => {
      return axios.post(`/admin/workspaces/${workspaceId}/branding/upload`, formData, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
      })
      .then(response => ({ response }))
      .catch(error => ({ error }));
    });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || `Failed to upload ${field}`);
      if (callback) callback(null, error);
    } else {
      EventBus.publish('success', `${field.charAt(0).toUpperCase() + field.slice(1)} uploaded successfully`);
      if (callback) callback(response['data']['body']);
    }
  } catch (error) {
    EventBus.publish('error', `Failed to upload ${payload['field']}`);
    if (payload['callback']) payload['callback'](null, error);
  }
}

// ===== DOMAIN VERIFICATION SAGAS =====

function* verifyCustomDomain({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Verifying domain...' }));
    const { error, response } = yield call(postCall, { path: `/admin/workspaces/${payload['workspaceId']}/domain/verify`, payload: {} });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Domain verification failed');
      if (payload['callback']) payload['callback'](null, error);
    } else {
      EventBus.publish('success', 'Domain verified successfully! Your custom domain is now active.');
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to verify domain');
    if (payload['callback']) payload['callback'](null, error);
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* getDomainInstructions({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['workspaceId']}/domain/instructions`);
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to load domain instructions');
      return;
    }
    
    yield put(setDomainInstructions(response['data']['body']));
    if (payload['callback']) payload['callback'](response['data']['body']);
  } catch (error) {
    EventBus.publish('error', 'Failed to load domain instructions');
    if (payload['callback']) payload['callback'](null, error);
  }
}

function* generateVerificationToken({ payload }) {
  try {
    const { error, response } = yield call(postCall, { path: `/admin/workspaces/${payload['workspaceId']}/domain/token`, payload: {} });
    
    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to generate verification token');
      if (payload['callback']) payload['callback'](null, error);
    } else {
      EventBus.publish('success', 'Verification token generated successfully');
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to generate verification token');
    if (payload['callback']) payload['callback'](null, error);
  }
}

// ===== SSL CERTIFICATE SAGAS =====

function* verifySSLCertificate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Verifying SSL certificate...' }));
    const { error, response } = yield call(postCall, { path: `/admin/workspaces/${payload['workspaceId']}/ssl/verify`, payload: {} });

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'SSL verification failed');
      if (payload['callback']) payload['callback'](null, error);
    } else {
      const result = response['data']['body'];
      if (result['validated']) {
        EventBus.publish('success', 'SSL certificate validated and activated successfully!');
      } else {
        EventBus.publish('warning', result['message'] || 'SSL certificate not yet validated. Please ensure DNS records are configured.');
      }
      if (payload['callback']) payload['callback'](result);
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to verify SSL certificate');
    if (payload['callback']) payload['callback'](null, error);
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* requestSSLCertificate({ payload }) {
  try {
    yield put(setAdminLoading({ isLoading: true, message: 'Requesting SSL certificate...' }));
    const { error, response } = yield call(postCall, { path: `/admin/workspaces/${payload['workspaceId']}/ssl/request`, payload: {} });

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to request SSL certificate');
      if (payload['callback']) payload['callback'](null, error);
    } else {
      EventBus.publish('success', 'SSL certificate requested successfully. Please add the DNS validation records.');
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
    yield put(setAdminLoading({ isLoading: false }));
  } catch (error) {
    EventBus.publish('error', 'Failed to request SSL certificate');
    if (payload['callback']) payload['callback'](null, error);
    yield put(setAdminLoading({ isLoading: false }));
  }
}

function* checkSSLStatus({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/${payload['workspaceId']}/ssl/status`);

    if (error) {
      if (payload['callback']) payload['callback'](null, error);
    } else {
      if (payload['callback']) payload['callback'](response['data']['body']);
    }
  } catch (error) {
    if (payload['callback']) payload['callback'](null, error);
  }
}

// ===== USER FEEDBACK MANAGEMENT =====

function* getAdminFeedback({ payload = {} }) {
  try {
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['type'] && { type: payload['type'] })
    });

    const { error, response } = yield call(getCall, `/admin/workspaces/feedback?${params}`);

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch feedback');
      yield put({ type: 'SET_ADMIN_FEEDBACK', payload: { feedback: [], pagination: {} } });
    } else {
      yield put({ type: 'SET_ADMIN_FEEDBACK', payload: response['data']['body'] });
    }
  } catch (error) {
    console.error('Get admin feedback error:', error);
    EventBus.publish('error', 'Failed to fetch feedback');
    yield put({ type: 'SET_ADMIN_FEEDBACK', payload: { feedback: [], pagination: {} } });
  }
}

function* getFeedbackStats() {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/feedback/stats`);

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch feedback stats');
    } else {
      yield put({ type: 'SET_FEEDBACK_STATS', payload: response['data']['body'] });
    }
  } catch (error) {
    console.error('Get feedback stats error:', error);
    EventBus.publish('error', 'Failed to fetch feedback stats');
  }
}

function* getAdminFeedbackById({ payload }) {
  try {
    const { error, response } = yield call(getCall, `/admin/workspaces/feedback/${payload['id']}`);

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch feedback details');
      yield put({ type: 'SET_SELECTED_FEEDBACK', payload: null });
    } else {
      yield put({ type: 'SET_SELECTED_FEEDBACK', payload: response['data']['body']['feedback'] });
    }
  } catch (error) {
    console.error('Get feedback by ID error:', error);
    EventBus.publish('error', 'Failed to fetch feedback details');
    yield put({ type: 'SET_SELECTED_FEEDBACK', payload: null });
  }
}

function* updateFeedbackStatus({ payload }) {
  try {
    const { error, response } = yield call(putCall, {
      path: `/admin/workspaces/feedback/${payload['id']}/status`,
      payload: { status: payload['status'] }
    });

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update feedback status');
    } else {
      EventBus.publish('success', 'Feedback status updated successfully');
      yield put({ type: 'FEEDBACK_UPDATED', payload: response['data']['body']['feedback'] });

      // Refresh stats so the pending cards and tab badge stay accurate
      yield put({ type: 'GET_FEEDBACK_STATS' });

      // Optionally refresh feedback list if filters provided
      if (payload['filters']) {
        yield put({ type: 'GET_ADMIN_FEEDBACK', payload: payload['filters'] });
      }
    }
  } catch (error) {
    console.error('Update feedback status error:', error);
    EventBus.publish('error', 'Failed to update feedback status');
  }
}

function* addAdminFeedbackResponse({ payload }) {
  try {
    const { error, response } = yield call(postCall, {
      path: `/admin/workspaces/feedback/${payload['id']}/response`,
      payload: { message: payload['message'] }
    });

    if (error) {
      EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to add response');
    } else {
      EventBus.publish('success', 'Response sent successfully. User will be notified via email.');
      yield put({ type: 'FEEDBACK_UPDATED', payload: response['data']['body']['feedback'] });

      // Refresh stats — responding moves pending tickets to in-review
      yield put({ type: 'GET_FEEDBACK_STATS' });

      // Optionally refresh feedback list if filters provided
      if (payload['filters']) {
        yield put({ type: 'GET_ADMIN_FEEDBACK', payload: payload['filters'] });
      }
    }
  } catch (error) {
    console.error('Add admin feedback response error:', error);
    EventBus.publish('error', 'Failed to add response');
  }
}

// ===== API HELPER FUNCTIONS =====

function getCall(path) {
  return axios.get(`${path}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  })
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

function postCall({ path, payload }) {
  return axios.post(`${path}`, payload, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  })
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

function putCall({ path, payload }) {
  return axios.put(`${path}`, payload, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  })
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

function deleteCall(path) {
  return axios.delete(`${path}`, {
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    }
  })
    .then(response => ({ response }))
    .catch(error => ({ error }));
}

// ===== USER FEEDBACK MANAGEMENT SAGAS - COMMENTED OUT - feedback only stored in DB, no admin display =====

/* function* getAdminFeedback({ payload = {} }) {
  try {
    yield put(setAdminLoading(true));
    const params = new URLSearchParams({
      page: payload['page'] || 1,
      limit: payload['limit'] || 20,
      ...(payload['search'] && { search: payload['search'] }),
      ...(payload['status'] && { status: payload['status'] }),
      ...(payload['type'] && { type: payload['type'] }),
      ...(payload['workspaceId'] && { workspaceId: payload['workspaceId'] })
    });

    const { error, response } = yield call(getCall, `/user/admin/feedback?${params}`);
    yield put(setAdminLoading(false));

    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch feedback');
    yield put({ type: 'SET_ADMIN_FEEDBACK', payload: response['data']['body'] });
  } catch (error) {
    yield put(setAdminLoading(false));
    EventBus.publish('error', 'Failed to fetch admin feedback');
  }
}

function* getAdminFeedbackById({ payload }) {
  try {
    yield put(setAdminLoading(true));
    const { error, response } = yield call(getCall, `/user/admin/feedback/${payload['id']}`);
    yield put(setAdminLoading(false));

    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to fetch feedback details');
    yield put({ type: 'SET_SELECTED_FEEDBACK', payload: response['data']['body'] });
  } catch (error) {
    yield put(setAdminLoading(false));
    EventBus.publish('error', 'Failed to fetch feedback details');
  }
}

function* updateFeedbackStatus({ payload }) {
  try {
    const { error, response } = yield call(putCall, {
      path: `/user/admin/feedback/${payload['id']}/status`,
      payload: { status: payload['status'] }
    });

    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update feedback status');

    EventBus.publish('success', 'Feedback status updated successfully');

    // Refresh feedback list
    yield put({ type: 'GET_ADMIN_FEEDBACK', payload: payload['filters'] || {} });

    // Update selected feedback if it was opened
    if (payload['updateSelected']) {
      yield put({ type: 'SET_SELECTED_FEEDBACK', payload: response['data']['body'] });
    }
  } catch (error) {
    EventBus.publish('error', 'Failed to update feedback status');
  }
}

function* addAdminFeedbackResponse({ payload }) {
  try {
    const { error, response } = yield call(putCall, {
      path: `/user/admin/feedback/${payload['id']}/response`,
      payload: { message: payload['message'] }
    });

    if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to add response');

    EventBus.publish('success', 'Response added and email sent to user successfully');

    // Update selected feedback
    yield put({ type: 'SET_SELECTED_FEEDBACK', payload: response['data']['body'] });

    // Refresh feedback list
    yield put({ type: 'GET_ADMIN_FEEDBACK', payload: payload['filters'] || {} });
  } catch (error) {
    EventBus.publish('error', 'Failed to add admin response');
  }
} */

// ===== ROOT SAGA =====

export default function* rootSaga() {
  yield all([
    // User Management
    takeEvery('GET_ADMIN_USERS', getAdminUsers),
    takeEvery('GET_ADMIN_USER_STATS', getAdminUserStats),
    takeEvery('EXPORT_ADMIN_USERS', exportAdminUsers),
    takeEvery('UPDATE_ADMIN_USER', updateAdminUser),
    takeEvery('CREATE_ADMIN_USER', createAdminUser),
    takeEvery('IMPERSONATE_USER', impersonateUser),
    takeEvery('EXIT_IMPERSONATION', exitImpersonation),
    takeEvery('DELETE_ADMIN_USER', deleteAdminUser),
    takeEvery('GET_DELETED_USERS', getDeletedUsers),
    takeEvery('RESTORE_USER', restoreUser),
    takeEvery('ACTIVATE_USER', activateUser),
    takeEvery('DEACTIVATE_USER', deactivateUser),
    takeEvery('SUSPEND_USER', deactivateUser),
    takeEvery('BULK_USER_ACTION', bulkUserAction),
    
    // Workspace Management
    takeEvery('GET_AVAILABLE_WORKSPACES', getAvailableWorkspaces),
    takeEvery('GET_WORKSPACE_PLANS', getWorkspacePlans),
    takeEvery('GET_ADMIN_WORKSPACES', getAdminWorkspaces),
    takeEvery('GET_WORKSPACE_STATS', getWorkspaceStats),
    takeEvery('EXPORT_ADMIN_WORKSPACES', exportAdminWorkspaces),
    takeEvery('UPDATE_ADMIN_WORKSPACE', updateAdminWorkspace),
    takeEvery('DELETE_ADMIN_WORKSPACE', deleteAdminWorkspace),
    takeEvery('GET_DELETED_WORKSPACES', getDeletedWorkspaces),
    takeEvery('RESTORE_WORKSPACE', restoreWorkspace),
    takeEvery('CREATE_ADMIN_WORKSPACE', createWorkspace),
    takeEvery('GET_WORKSPACE_BY_ID', getWorkspaceById),
    takeEvery('GET_WORKSPACE_ACTIVITY', getWorkspaceActivity),
    takeEvery('SUSPEND_WORKSPACE', suspendWorkspace),
    takeEvery('UNSUSPEND_WORKSPACE', unsuspendWorkspace),
    takeEvery('CHANGE_WORKSPACE_PLAN', changeWorkspacePlan),
    takeEvery('RESET_WORKSPACE_USAGE', resetWorkspaceUsage),
    takeEvery('BULK_WORKSPACE_ACTION', bulkWorkspaceAction),
    
    // Workspace Details Sidebar
    takeEvery('GET_WORKSPACE_MEMBERS', getWorkspaceMembers),
    takeEvery('GET_WORKSPACE_CONTACTS', getWorkspaceContacts),
    takeEvery('GET_WORKSPACE_DEALS', getWorkspaceDeals),
    takeEvery('GET_WORKSPACE_AGENTS', getWorkspaceAgents),
    takeEvery('GET_OWNER_WORKSPACES', getOwnerWorkspaces),
    takeEvery('GET_ACCOUNT_AGENTS', getAccountAgents),
    takeEvery('GET_WORKSPACE_PAYMENT_HISTORY', getWorkspacePaymentHistory),
    
    // Billing Management
    takeEvery('GET_ADMIN_SUBSCRIPTIONS', getAdminSubscriptions),
    takeEvery('GET_ALL_SUBSCRIPTIONS_FOR_EXPORT', getAllSubscriptionsForExport),
    takeEvery('GET_SUBSCRIPTION_STATS', getSubscriptionStats),
    takeEvery('GET_SUBSCRIPTION_BY_ID', getSubscriptionById),
    takeEvery('CANCEL_SUBSCRIPTION', cancelSubscription),
    takeEvery('REACTIVATE_SUBSCRIPTION', reactivateSubscription),
    takeEvery('APPLY_CREDIT', applyCredit),
    takeEvery('BULK_CANCEL_SUBSCRIPTIONS', bulkCancelSubscriptions),
    takeEvery('BULK_REACTIVATE_SUBSCRIPTIONS', bulkReactivateSubscriptions),
    takeEvery('BULK_APPLY_CREDITS', bulkApplyCredits),
    takeEvery('GET_PAYMENTS_ADMIN', getPayments),
    takeEvery('PROCESS_PAYMENT_REFUND', processPaymentRefund),
    takeEvery('GET_REVENUE_ANALYTICS', getRevenueAnalytics),
    takeEvery('GET_MRR_ANALYTICS', getMRRAnalytics),
    takeEvery('GET_PLANS_OVERVIEW', getPlansOverview),
    takeEvery('GET_BILLING_OVERVIEW', getBillingOverview),
    takeEvery('GET_AGENCY_PLAN_PERFORMANCE', getAgencyPlanPerformance),
    takeEvery('GET_USER_ACCESS_TYPE', getUserAccessType),

    // Plan Management
    takeEvery('GET_ADMIN_PLANS', getAdminPlans),
    takeEvery('UPDATE_PLAN', updatePlan),
    takeEvery('TOGGLE_PLAN_STATUS', togglePlanStatus),
    
    // Subscription Details Sidebar
    takeEvery('GET_SUBSCRIPTION_PAYMENTS', getSubscriptionPayments),
    takeEvery('GET_SUBSCRIPTION_ACTIVITY', getSubscriptionActivity),
    takeEvery('GET_SUBSCRIPTION_USAGE_HISTORY', getSubscriptionUsageHistory),
    takeEvery('APPLY_SUBSCRIPTION_CREDIT', applySubscriptionCredit),
    
    // Agent Management
    takeEvery('GET_ADMIN_AGENTS', getAdminAgents),
    takeEvery('GET_AGENT_STATS', getAgentStats),
    takeEvery('EXPORT_ADMIN_AGENTS', exportAdminAgents),
    takeEvery('GET_AGENT_BY_ID', getAgentById),
    takeEvery('ACTIVATE_AGENT', activateAgent),
    takeEvery('DEACTIVATE_AGENT', deactivateAgent),
    takeEvery('BULK_UPDATE_AGENT_STATUS', bulkUpdateAgentStatus),
    takeEvery('START_AGENT', startAgent),
    takeEvery('STOP_AGENT', stopAgent),
    takeEvery('RESTART_AGENT', restartAgent),
    takeEvery('GET_AGENT_LOGS', getAgentLogs),
    takeEvery('GET_AGENT_CALLS', getAgentCalls),
    takeEvery('BULK_AGENT_ACTION', bulkAgentAction),
    takeEvery('GET_PERFORMANCE_METRICS', getPerformanceMetrics),
    takeEvery('GET_COST_ANALYTICS', getCostAnalytics),
    
    // System Monitoring
    takeEvery('GET_SYSTEM_HEALTH', getSystemHealth),
    takeEvery('GET_SYSTEM_SERVICES', getSystemServices),
    takeEvery('GET_SYSTEM_METRICS', getSystemMetrics),
    takeEvery('GET_SYSTEM_ALERTS', getSystemAlerts),
    takeEvery('GET_SYSTEM_PERFORMANCE', getSystemPerformance),
    takeEvery('GET_AGENT_PERFORMANCE', getAgentPerformance),
    
    // Dashboard
    takeEvery('GET_ADMIN_DASHBOARD', getAdminDashboard),
    takeEvery('GET_AGENT_HEALTH_ONLY', getAgentHealthOnly),
    
    // Security & Compliance
    takeEvery('GET_AUDIT_LOGS', getAuditLogs),
    takeEvery('GET_SECURITY_SETTINGS', getSecuritySettings),
    takeEvery('UPDATE_SECURITY_SETTINGS', updateSecuritySettings),
    
    // Credit Management
    takeEvery('ADD_CREDITS', addCredits),
    takeEvery('GET_CREDIT_HISTORY', getCreditHistory),
    takeEvery('GET_ZAPMAIL_BILLING', getZapmailBilling),
    takeEvery('LOOKUP_ZAPMAIL_WORKSPACE', lookupZapmailWorkspace),
    takeEvery('GET_ALL_WORKSPACES', getAllWorkspaces),
    takeEvery('GET_WORKSPACE_CREDITS', getWorkspaceCredits),
    takeEvery('CREATE_API_KEY', createApiKeySaga),
    takeEvery('REVOKE_API_KEY', revokeApiKeySaga),
    takeEvery('ADD_WHITELIST_IP', addWhitelistIpSaga),
    takeEvery('REMOVE_WHITELIST_IP', removeWhitelistIpSaga),
    
    // Compliance
    takeEvery('GET_COMPLIANCE_STATUS', getComplianceStatus),
    takeEvery('UPDATE_GDPR_COMPLIANCE', updateGDPRCompliance),
    takeEvery('UPDATE_DATA_RETENTION', updateDataRetention),
    takeEvery('GET_DATA_REQUESTS', getDataRequestsSaga),
    takeEvery('CREATE_DATA_REQUEST', createDataRequest),
    takeEvery('PROCESS_DATA_REQUEST', processDataRequestSaga),
    takeEvery('GENERATE_COMPLIANCE_REPORT', generateComplianceReport),
    
    // Analytics
    takeEvery('GET_ANALYTICS_DATA', getAnalyticsData),
    takeEvery('GET_CUSTOM_REPORT', getCustomReport),
    takeEvery('GET_REPORTS', getReports),
    takeEvery('EXPORT_REPORT', exportReport),
    
    // Email Templates
    takeEvery('GET_EMAIL_TEMPLATES', getEmailTemplates),
    takeEvery('GET_TEMPLATE_STATS', getTemplateStats),
    takeEvery('CREATE_EMAIL_TEMPLATE', createEmailTemplate),
    takeEvery('UPDATE_EMAIL_TEMPLATE', updateEmailTemplate),
    takeEvery('DELETE_EMAIL_TEMPLATE', deleteEmailTemplate),
    takeEvery('ACTIVATE_TEMPLATE', activateTemplate),
    takeEvery('DEACTIVATE_TEMPLATE', deactivateTemplate),
    takeEvery('DUPLICATE_TEMPLATE', duplicateTemplate),
    takeEvery('BULK_ACTIVATE_TEMPLATES', bulkActivateTemplates),
    takeEvery('BULK_DEACTIVATE_TEMPLATES', bulkDeactivateTemplates),
    takeEvery('BULK_DELETE_TEMPLATES', bulkDeleteTemplates),
    takeEvery('BULK_CHANGE_TEMPLATE_CATEGORY', bulkChangeTemplateCategory),
    takeEvery('BULK_EXPORT_TEMPLATES', bulkExportTemplates),
    
    // Activity Logs
    takeEvery('GET_ACTIVITY_LOGS', getActivityLogs),
    takeEvery('GET_ACTIVITY_STATS', getActivityStats),
    takeEvery('GET_USER_ACTIVITIES', getUserActivities),
    takeEvery('CLEAR_OLD_LOGS', clearOldLogs),
    takeEvery('ARCHIVE_LOGS', archiveLogs),

    // User Details Sidebar
    takeEvery('GET_USER_WORKSPACES', getUserWorkspaces),
    takeEvery('GET_USER_BILLING_HISTORY', getUserBillingHistory),
    takeEvery('GET_USER_SECURITY_LOGS', getUserSecurityLogs),
    
    // System Configuration
    takeEvery('GET_SYSTEM_CONFIG', getSystemConfig),
    takeEvery('SAVE_SYSTEM_CONFIG', saveSystemConfig),
    takeEvery('RESET_CONFIG_CATEGORY', resetConfigCategory),
    takeEvery('TEST_SERVICE_CONNECTION', testServiceConnection),
    
    // Backup & Restore
    takeEvery('GET_BACKUPS', getBackups),
    takeEvery('GET_BACKUP_STATS', getBackupStats),
    takeEvery('GET_RESTORE_HISTORY', getRestoreHistory),
    takeEvery('CREATE_BACKUP', createBackup),
    takeEvery('RESTORE_BACKUP', restoreBackup),
    takeEvery('DELETE_BACKUP', deleteBackup),
    takeEvery('DOWNLOAD_BACKUP', downloadBackup),
    takeEvery('UPLOAD_BACKUP', uploadBackup),
    takeEvery('CLEANUP_OLD_BACKUPS', cleanupOldBackups),
    takeEvery('VERIFY_ALL_BACKUPS', verifyAllBackups),
    takeEvery('REBUILD_BACKUP_INDEX', rebuildBackupIndex),
    
    // Additional Bulk Actions
    takeEvery('DELETE_BULK_USERS', deleteBulkUsers),
    takeEvery('SEND_BULK_EMAIL', sendBulkEmail),
    takeEvery('BULK_EMAIL_SUBSCRIPTIONS', bulkEmailSubscriptions),
    takeEvery('BULK_RESET_WORKSPACE_USAGE', bulkResetWorkspaceUsage),
    takeEvery('BULK_DELETE_WORKSPACES', bulkDeleteWorkspaces),
    takeEvery('BULK_ASSIGN_WORKSPACE', bulkAssignWorkspace),
    takeEvery('SEND_WORKSPACE_NOTIFICATION', sendWorkspaceNotification),
    takeEvery('GET_NOTIFICATION_HISTORY', getNotificationHistory),
    
    // Workspace Branding
    takeEvery('GET_WORKSPACE_BRANDING', getWorkspaceBranding),
    takeEvery('UPDATE_WORKSPACE_BRANDING', updateWorkspaceBranding),
    takeEvery('UPLOAD_BRANDING_ASSET', uploadBrandingAsset),
    
    // Domain Verification
    takeEvery('VERIFY_CUSTOM_DOMAIN', verifyCustomDomain),
    takeEvery('GET_DOMAIN_INSTRUCTIONS', getDomainInstructions),
    takeEvery('GENERATE_VERIFICATION_TOKEN', generateVerificationToken),

    // SSL Certificate
    takeEvery('REQUEST_SSL_CERTIFICATE', requestSSLCertificate),
    takeEvery('VERIFY_SSL_CERTIFICATE', verifySSLCertificate),
    takeEvery('CHECK_SSL_STATUS', checkSSLStatus),

    // User Feedback Management
    takeEvery('GET_ADMIN_FEEDBACK', getAdminFeedback),
    takeEvery('GET_FEEDBACK_STATS', getFeedbackStats),
    takeEvery('GET_ADMIN_FEEDBACK_BY_ID', getAdminFeedbackById),
    takeEvery('UPDATE_FEEDBACK_STATUS', updateFeedbackStatus),
    takeEvery('ADD_ADMIN_FEEDBACK_RESPONSE', addAdminFeedbackResponse),
  ]);
}